import {
  type Component,
  createEffect,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import AliasSettings from "./AliasSettings";
import BundleReadout from "./BundleReadout";
import DeleteAccountModal from "./DeleteAccountModal";
import IgnoresSettings from "./IgnoresSettings";
import InlineConfirmButton from "./InlineConfirmButton";
import { windowCandidates } from "./lib/activeWindows";
import { ApiError, displayNick, type Network, visitorNetworkNick } from "./lib/api";
import { getSubject, token } from "./lib/auth";
import { autoAwayDebounceValue, loadAutoAwayDebounce, saveAutoAwayDebounce } from "./lib/autoAway";
import { createBackdropDismiss } from "./lib/backdropDismiss";
import { playBeep } from "./lib/beep";
import { getBoldMentions } from "./lib/boldMentions";
import { type ChannelKey, decodeChannelKey } from "./lib/channelKey";
import { getColoredNicklist } from "./lib/colorNicklist";
import {
  windowMuteKey,
  withConversationMute,
  withoutConversationMute,
} from "./lib/conversationMute";
import { CREDITS_LABEL, openCreditsModal } from "./lib/creditsModal";
import {
  syncedSetBoldMentions,
  syncedSetColoredNicklist,
  syncedSetShowBottomBar,
  syncedSetShowEventBadge,
  syncedSetStripFormatting,
  syncedSetTimeFormat,
} from "./lib/displayPrefs";
import { formatDuration } from "./lib/duration";
import { getShowEventBadge } from "./lib/eventBadge";
import {
  FONT_SIZE_MAX_PX,
  FONT_SIZE_MIN_PX,
  FONT_SIZE_PRESETS,
  getFontSizePx,
  setFontSizePx,
} from "./lib/fontSize";
import { errorMessage, friendlyApiError } from "./lib/friendlyApiError";
import { getHideNextActive, setHideNextActive } from "./lib/hideNextActive";
import {
  autoAwayReasonValue,
  loadAutoAwayReason,
  loadQuitPartReason,
  quitPartReasonValue,
  saveAutoAwayReason,
  saveQuitPartReason,
} from "./lib/leaveReasons";
import {
  deleteAccountBody,
  deleteAvatar,
  updateIdentity,
  updateNetworkPassword,
  updateProfile,
  uploadAvatar,
} from "./lib/lifecycle";
import { networks, user } from "./lib/networks";
import { mirrorNotificationPrefs, notificationPrefs } from "./lib/notificationPrefs";
import {
  DEFAULT_NOTIFICATION_SOUND,
  NOTIFICATION_SOUND_PRESETS,
  NOTIFICATION_SOUNDS,
  type NotificationSound,
} from "./lib/notificationSound";
import { popOverlay, pushOverlay } from "./lib/overlayScrollLock";
import {
  loadShowPeerProfiles,
  saveShowPeerProfiles,
  showPeerProfilesValue,
} from "./lib/peerProfiles";
import {
  deletePushSubscription,
  deviceRows,
  disablePush,
  type EnablePushResult,
  enablePush,
  formatDeviceActivity,
  listPushDevices,
  type PushDeviceSummary,
  pushAvailable,
  renamePushDevice,
  type SubscriptionId,
  subscriptionIdForEndpoint,
} from "./lib/push";
import { reconnectConnectedNetworks } from "./lib/reconnect";
import { selectedChannel } from "./lib/selection";
import { consumePendingSettingsPage, type SettingsSubPage } from "./lib/settingsNav";
import { isShareableSubject, openShareModal, SHARE_SESSION_LABEL } from "./lib/shareModal";
import { getShowBottomBar } from "./lib/showBottomBar";
import { getStripFormatting } from "./lib/stripFormatting";
import { getTimeFormat, type TimeFormatKey } from "./lib/timeFormat";
import { activeHost } from "./lib/uploadHost";
import {
  loadUploadConfirmEnabled,
  loadUploadTtlSeconds,
  saveUploadConfirmEnabled,
  saveUploadTtlSeconds,
  uploadConfirmEnabledValue,
  uploadTtlSecondsValue,
} from "./lib/uploadOrchestrator";
import { deviceClassIcon, deviceDisplayName, parseUserAgent } from "./lib/userAgent";
import {
  getNotificationPrefs,
  getVhostSettings,
  type MutedTargets,
  type NotificationPrefs,
  putNotificationPrefs,
  putVhostSelection,
  type VhostSettingsView,
} from "./lib/userSettings";
import {
  getVideoProcessingEnabled,
  setVideoProcessingEnabled,
  VIDEO_PROCESSING_LABEL,
} from "./lib/videoProcessing";
import PerformSettings from "./PerformSettings";
import ThemeGallery from "./ThemeGallery";
import TotpSettings from "./TotpSettings";
import VhostSettingsPage from "./VhostSettingsPage";
import WatchlistsSettings from "./WatchlistsSettings";

// Right-overlay drawer. #460 — the main page is now an INDEX of nav rows;
// each row pushes into a dedicated sub-page. Some sub-pages are separate
// components (themes, watch lists, aliases, perform, vhost); three are INLINE
// `<Show>` blocks whose signals live in this component's body (general =
// upload retention + visitor identity; display = text size / timestamp /
// colored nicklist; push = notifications). The main page keeps, BELOW the
// index, the subject-gated affordances that aren't sub-pages: the share entry,
// delete account, done. (#986 retired three of them: admin console was an
// exact duplicate of the rail's admin action, and detach + quit moved into
// the rail actions menu behind a per-subject confirm modal.)
//
// open prop drives the .open class; the drawer stays mounted across
// open/close so onMount-loaded state (devices + prefs) doesn't refetch
// per open. Backdrop click fires onClose; Esc closes it via the
// keybindings drawer fallback (Shell.tsx closeDrawer) — the drawer is a
// scroll-lock-only overlay, NOT in the #232 modal ESC stack, so the
// delete-account modal opened FROM the drawer closes on the first Esc and
// the drawer itself on the next. (#392 — the share surface is a MODAL again,
// mounted in Shell and closed via the shared overlay Esc stack, NOT a drawer
// sub-page; the drawer's share entry just calls openShareModal.)

export type Props = {
  open: boolean;
  onClose: () => void;
};

// #348 — the auto-away ladder the control offers. Deliberately coarse:
// these are the answers to "how long before my friends see me as away",
// and anything between the rungs is what the custom entry is for. The
// site-default entry carries NO number — cic does not know the server's
// constant and a copy here would go stale the day it moves.
const AUTO_AWAY_PRESETS = [
  { seconds: 60, label: "1 minute" },
  { seconds: 300, label: "5 minutes" },
  { seconds: 600, label: "10 minutes" },
  { seconds: 1800, label: "30 minutes" },
  { seconds: 3600, label: "1 hour" },
];

const SettingsDrawer: Component<Props> = (props) => {
  const [fontPx, setFontPx] = createSignal<number>(getFontSizePx());
  const [timeFmt, setTimeFmt] = createSignal<TimeFormatKey>(getTimeFormat());
  const [coloredNicklist, setColoredNicklistSig] = createSignal<boolean>(getColoredNicklist());
  // issue 2157 — device-local, so the checkbox's state is a plain local signal
  // seeded from storage, exactly as `size` above is. No module signal is needed:
  // `videoProcessing.ts` is read once per upload attempt, never in a render path.
  const [videoProcessing, setVideoProcessing] = createSignal<boolean>(getVideoProcessingEnabled());

  const [devices, setDevices] = createSignal<PushDeviceSummary[]>([]);
  // #964 — the server row THIS browser registered, so its list entry can say
  // so. Proven by endpoint match (`subscriptionIdForEndpoint`), never guessed
  // from the UA: two same-browser rows are byte-identical, which is the whole
  // reason this issue exists. `null` = we cannot prove any row is ours (never
  // subscribed here / cleared site data) — then NO row gets the marker.
  const [currentDeviceId, setCurrentDeviceId] = createSignal<SubscriptionId | null>(null);
  // #964 — inline rename. `renamingId` is the row currently in edit mode
  // (at most one); the draft lives here rather than in the input so a
  // re-render from a background refreshDevices cannot discard the typing.
  const [renamingId, setRenamingId] = createSignal<SubscriptionId | null>(null);
  const [renameDraft, setRenameDraft] = createSignal("");
  const [renameError, setRenameError] = createSignal<string | null>(null);
  const [pushEnabled, setPushEnabled] = createSignal(false);
  const [pushBanner, setPushBanner] = createSignal<string | null>(null);
  const [savingPrefs, setSavingPrefs] = createSignal(false);
  const [prefsError, setPrefsError] = createSignal<string | null>(null);
  // UX-4 bucket M (2026-05-19) — upload-TTL signals. Server is the
  // authoritative source; loadUploadTtlSeconds populates the cic
  // cache on drawer mount, saveUploadTtlSeconds round-trips on
  // change. `null` = "use the active host's defaultTtl".
  const [uploadTtlSavingError, setUploadTtlSavingError] = createSignal<string | null>(null);
  const [uploadConfirmSavingError, setUploadConfirmSavingError] = createSignal<string | null>(null);
  // #348 — auto-away debounce. The server owns the behaviour AND the
  // accepted range; these three only drive the control. `customMode`
  // is a MODE the user can enter without having written anything yet,
  // which is why it is a signal and not derived from the stored value
  // alone.
  const [autoAwaySavingError, setAutoAwaySavingError] = createSignal<string | null>(null);
  // M2 — the peer-profiles opt-in. Boolean, no custom-value mode.
  const [showPeerProfilesSavingError, setShowPeerProfilesSavingError] = createSignal<string | null>(
    null,
  );
  const [autoAwayCustomMode, setAutoAwayCustomMode] = createSignal(false);
  const [autoAwayCustomDraft, setAutoAwayCustomDraft] = createSignal("");

  // issue 2150 — the two remembered leave reasons. Each keeps a DRAFT
  // separate from the stored value: these are free-text inputs saved on a
  // button, not selects that commit on change, so the field has to hold
  // what the user is typing without claiming it is persisted.
  //
  // `null` (nothing stored) seeds an EMPTY draft, and emptying the draft
  // clears the setting — the server maps `""` to "no default", so the two
  // ends of the round trip agree with no sentinel of cic's own.
  const [quitPartReasonDraft, setQuitPartReasonDraft] = createSignal<string | null>(null);
  const [quitPartReasonSavingError, setQuitPartReasonSavingError] = createSignal<string | null>(
    null,
  );
  const [autoAwayReasonDraft, setAutoAwayReasonDraft] = createSignal<string | null>(null);
  const [autoAwayReasonSavingError, setAutoAwayReasonSavingError] = createSignal<string | null>(
    null,
  );
  // #228, #251 — source-bind (vhost) selection. Server owns the allow-set +
  // current selection (no admin pin — #251). `null` view = not-yet-loaded
  // (the widget stays hidden until the first GET lands).
  const [vhostView, setVhostView] = createSignal<VhostSettingsView | null>(null);
  const [vhostError, setVhostError] = createSignal<string | null>(null);
  // #282 — explicit "Reconnect to apply" state for the vhost sub-page. The
  // vhost is inert until the upstream reconnects; the footer button bounces
  // the connected networks. `reconnecting` is the in-flight/double-fire
  // guard + drives the button label; `reconnectError` surfaces a failure.
  const [reconnecting, setReconnecting] = createSignal(false);
  const [reconnectError, setReconnectError] = createSignal<string | null>(null);
  // #252 — settings sub-page navigation. The drawer is an index page ("main")
  // that can push into a dedicated sub-page; the pattern mirrors AdminPane's
  // tab signal. cic never originates vhost state — the sub-page reads
  // `vhostView` + reports changes up via the same save-on-change PUT flow.
  const [settingsPage, setSettingsPage] = createSignal<SettingsSubPage>("main");
  // #1306 — gate for the share-session entry. The share used to be
  // visitor-only ("a user has no portable session to share"); the server
  // mints for a password subject now, so the rule left is #363's: an
  // incognito (ephemeral) session must not be made portable. Delegated to
  // `isShareableSubject` so the home-pane door answers identically; what
  // stays local is the SOURCE, the persisted subject. #476 — the identity
  // editor is NOT gated here (it moved to hasNetworks()), so this is the
  // only thing the subject kind still decides in this drawer.
  const canShareSession = (): boolean => isShareableSubject(getSubject());
  // #986 — the `showDetach()` gate + the two-tap `quitArmed` latch moved to
  // the rail with the buttons they served (`canDetach()` in lib/lifecycle
  // asks the same `isPersistentIdentity` question; the latch has no
  // successor — the shared confirm modal replaced the two-tap arm).
  // #157 — "delete account" is an IRREVERSIBLE total wipe, surfaced as a
  // SEPARATE affordance from quit (quit PRESERVES a persistent identity;
  // delete nukes it). It opens a confirm MODAL (type-your-name gate) — the
  // ONE typed gate in the product (#986: detach and quit explain and ask,
  // this one asks you to spell the identity out). Offered ONLY to a registered
  // NON-admin user or a registered visitor; admins (issue #157) + anon
  // visitors are excluded. Reads the reactive `/me` resource (authoritative
  // for is_admin / registered) so a mid-session demote/refetch flips it.
  const [deleteOpen, setDeleteOpen] = createSignal(false);
  const showDeleteAccount = (): boolean => {
    const u = user();
    if (!u) return false;
    if (u.kind === "user") return u.is_admin === false;
    return u.registered === true;
  };
  // The exact string the operator must type to arm deletion — account name
  // (user) or, for a visitor (no identity-wide nick), the SELECTED network
  // row's nick. #478 — the retired lowest-id "anchor" pick is gone: the
  // confirm nick follows the network the editor targets (focused-network
  // default), keeping the kind==="visitor" narrow via `visitorNetworkNick`.
  // Empty when /me or /networks hasn't loaded (the button is withheld then).
  const deleteConfirmationText = (): string => {
    const u = user();
    if (!u) return "";
    if (u.kind === "user") return displayNick(u);
    return visitorNetworkNick(selectedIdentityNetwork()) ?? "";
  };
  // Comma-separated UI shadows for the two whitelist text inputs — the
  // server stores normalized lists; cic edits are joined with ", " and
  // re-split on PUT so partial typing doesn't drop characters.
  const [channelsOnlyText, setChannelsOnlyText] = createSignal("");
  const [nicksOnlyText, setNicksOnlyText] = createSignal("");

  // issue 2164 — a preset rung carries its NUMBER in `value`, so picking one
  // and typing one go through the same setter and there is no key left to
  // translate.
  const onFontSizePresetChange = (e: Event) => {
    const px = Number((e.currentTarget as HTMLInputElement).value);
    setFontPx(setFontSizePx(px));
  };

  // issue 2164 — the advanced field. `setFontSizePx` returns what it ACTUALLY
  // applied, and the box is made to show that: a reactive `value=` alone
  // cannot, because a number clamping onto the size already in effect leaves
  // the signal unchanged and re-renders nothing — the box would keep
  // displaying a number that never took.
  const onFontSizeCustomChange = (e: Event) => {
    const box = e.currentTarget as HTMLInputElement;
    // Same reason `onAutoAwayCustomSave` refuses a blank: `Number("")` is 0,
    // which the clamp would turn into the 9px floor. An emptied box is not an
    // instruction, so it re-states what is in effect instead of applying one.
    if (box.value.trim() === "") {
      box.value = String(fontPx());
      return;
    }
    const applied = setFontSizePx(Number(box.value));
    setFontPx(applied);
    box.value = String(applied);
  };

  const onTimeFormatChange = (e: Event) => {
    const value = (e.currentTarget as HTMLInputElement).value as TimeFormatKey;
    setTimeFmt(value); // local signal for optimistic drawer UI
    syncedSetTimeFormat(value); // #449 — local apply + server PUT (converges devices)
  };

  const onColoredNicklistChange = (e: Event) => {
    const on = (e.currentTarget as HTMLInputElement).checked;
    setColoredNicklistSig(on); // local signal for optimistic drawer UI
    syncedSetColoredNicklist(on); // #449 — local apply + server PUT
  };

  // #914 — no drawer-local mirror signal (unlike the nicklist row above):
  // `getHideNextActive()` is already the module signal, so the checkbox binds
  // straight to it and there is no second copy to keep in sync.
  const onHideNextActiveChange = (e: Event) => {
    setHideNextActive((e.currentTarget as HTMLInputElement).checked);
  };

  // #1766 — like the #914 row, no drawer-local mirror: `getShowBottomBar()` IS
  // the module signal. Unlike it, the write goes through the coordinator, which
  // is the single PUT authority for the #449 synced prefs — the owner module's
  // own setter stays local-only on purpose.
  const onShowBottomBarChange = (e: Event) => {
    syncedSetShowBottomBar((e.currentTarget as HTMLInputElement).checked);
  };

  // #2029 — same shape as the row above: `getStripFormatting()` IS the module
  // signal, so no drawer-local mirror, and the write goes through the
  // coordinator (the single PUT authority for the #449 synced prefs).
  const onStripFormattingChange = (e: Event) => {
    syncedSetStripFormatting((e.currentTarget as HTMLInputElement).checked);
  };

  // #2037 B — same shape again. The one difference worth knowing is the
  // DEFAULT: this is the first synced display pref that ships OFF and thereby
  // removes something an operator already sees.
  const onShowEventBadgeChange = (e: Event) => {
    syncedSetShowEventBadge((e.currentTarget as HTMLInputElement).checked);
  };

  // issue 2167 — same shape as the three rows above, default back to ON: this
  // is an opt-OUT, so an unchecked box is the reader asking for the change and
  // a checked one is today's appearance.
  const onBoldMentionsChange = (e: Event) => {
    syncedSetBoldMentions((e.currentTarget as HTMLInputElement).checked);
  };

  // #986 — the `onDetach` / `onQuit` handlers moved to RailActions with
  // their buttons, and now fire through lib/lifecycle's `confirmDetach` /
  // `confirmQuit` so the modal states the per-subject consequence first.

  // #211 phase 6 — the #126 disconnect ⇄ reconnect handlers are RETIRED
  // (per-network park/reconnect moved to the home page; global disconnect
  // is `quit`). The `visitorConnected()` accessor (read the singular /me
  // `connected` scalar) went with them — the scalar is dropped from /me.

  // #476 / #478 — per-network identity editor (nick + ident + realname),
  // live-applied via PATCH /networks/:slug/identity → internal reconnect.
  // Available to BOTH subjects: the visitor-only gate was a retired-premise
  // relic ("users have no per-network identity"). A user carries per-network
  // identity on its GET /networks rows too (both subjects converged, ruling
  // A). The editor targets the SELECTED network row, defaulting to the
  // currently-focused network, with a picker to switch when the subject holds
  // more than one — killing the #211-phase-7 lowest-id "anchor" pick (#478).
  // The text shadows seed from the selected row on open; a save PATCHes then
  // refetches. A 422 (bad nick/ident) surfaces inline via `identityError`.
  const identityNetworks = (): Network[] => networks() ?? [];
  const hasNetworks = (): boolean => identityNetworks().length > 0;

  const [selectedIdentitySlug, setSelectedIdentitySlug] = createSignal<string | null>(null);
  // The network row the editor currently targets: the explicitly-selected
  // slug, else the first row (a stable fallback until the open-seed lands).
  const selectedIdentityNetwork = (): Network | null => {
    const list = identityNetworks();
    return list.find((n) => n.slug === selectedIdentitySlug()) ?? list[0] ?? null;
  };

  const [nickText, setNickText] = createSignal("");
  const [identText, setIdentText] = createSignal("");
  const [realnameText, setRealnameText] = createSignal("");
  const [identitySaving, setIdentitySaving] = createSignal(false);
  const [identityError, setIdentityError] = createSignal<string | null>(null);
  // issue 1993 — what the ONE apply did, as a closed set rather than a
  // boolean: "unchanged" is a real outcome now that the button no longer
  // bounces a card nobody edited, and a silent no-op after two taps reads as
  // a broken button.
  const [applyResult, setApplyResult] = createSignal<"applied" | "unchanged" | null>(null);
  // Two-tap arm for the apply button (parent owns the flag per
  // InlineConfirmButton's contract) — the reconnect is disruptive
  // (session bounces), so it arms rather than firing on the first tap. #986
  // retired the settings copies of this control for the LIFECYCLE verbs only
  // — as a per-row apply gate it is unchanged, here and at its ~20 other
  // call sites. issue 1993 renamed it off `identity`: it now arms the
  // password write too.
  const [applyArmed, setApplyArmed] = createSignal(false);

  // KVIrc-style CTCP USERINFO profile (age/gender/location/languages/a free
  // custom field). Targets the SAME selected network the identity editor
  // above does — no separate picker. Unlike identity, saving does NOT
  // reconnect (these fields never ride the IRC handshake), so there's no
  // two-tap confirm here — a plain save.
  const [profileAge, setProfileAge] = createSignal("");
  const [profileGender, setProfileGender] = createSignal("");
  const [profileLocation, setProfileLocation] = createSignal("");
  const [profileLanguages, setProfileLanguages] = createSignal("");
  const [profileCustom, setProfileCustom] = createSignal("");
  const [profileSaving, setProfileSaving] = createSignal(false);
  const [profileError, setProfileError] = createSignal<string | null>(null);
  const [profileSaved, setProfileSaved] = createSignal(false);

  // M3a — the own avatar, on the SAME selected network the profile editor
  // above does. No text signal for the value itself: the current avatar is
  // read straight off `net.avatar_url` (server-authoritative, like every
  // other credential field here) — these signals only track the upload
  // widget's transient in-flight state.
  const [avatarUploading, setAvatarUploading] = createSignal(false);
  const [avatarError, setAvatarError] = createSignal<string | null>(null);

  // #124 — the per-network PASSWORD field: write-only, leave-blank-to-keep.
  //
  // issue 1993 folded its SAVE into the identity apply above, reversing what
  // #124 wrote here ("NOT folded into the identity form … one Save over both
  // would make an untouched password field indistinguishable from 'clear my
  // password'"). That objection is answered rather than overruled: the
  // blank-keeps rule lives in `onApply` on the PASSWORD leg, which simply
  // does not call the endpoint for `""`, so a blank still cannot clear
  // anything. What made two buttons wrong is that both bounce the session,
  // so a user changing nick AND password paid two reconnects for one
  // intention. The signals stay separate because the two CALLS are separate
  // (distinct endpoints, distinct failures); only the gesture merged.
  const [passwordText, setPasswordText] = createSignal("");
  const [passwordSaving, setPasswordSaving] = createSignal(false);
  const [passwordError, setPasswordError] = createSignal<string | null>(null);

  // Default the editor's target ONCE per open-session: the currently-focused
  // network (if it resolves to one of the subject's rows), else the first
  // row. `identitySeeded` latches so a later /networks refetch never re-picks
  // the target out from under an in-progress edit. Reset on close (see the
  // close effect).
  const [identitySeeded, setIdentitySeeded] = createSignal(false);
  createEffect(() => {
    if (!props.open || identitySeeded()) return;
    const list = identityNetworks();
    const focusedSlug = selectedChannel()?.networkSlug ?? null;
    const defaultNet = list.find((n) => n.slug === focusedSlug) ?? list[0];
    if (!defaultNet) return;
    setSelectedIdentitySlug(defaultNet.slug);
    setIdentitySeeded(true);
  });

  // (Re-)seed the text fields whenever the TARGET network changes — the
  // initial default above, or a manual pick from the selector. `on` tracks
  // ONLY the slug, so a /networks refetch of the same target doesn't re-fire
  // and clobber in-progress typing. Clears transient save state on switch.
  createEffect(
    on(selectedIdentitySlug, (slug) => {
      if (slug === null) return;
      const net = identityNetworks().find((n) => n.slug === slug);
      if (!net) return;
      setNickText(net.nick);
      setIdentText(net.ident ?? "");
      setRealnameText(net.realname ?? "");
      // issue 1993 — a typed password belongs to the network it was typed
      // FOR. The field now shares the identity card's target, so carrying it
      // across a switch would write one network's secret to another.
      setPasswordText("");
      setPasswordError(null);
      setApplyArmed(false);
      setApplyResult(null);
      setIdentityError(null);
      setProfileAge(net.age ?? "");
      setProfileGender(net.gender ?? "");
      setProfileLocation(net.location ?? "");
      setProfileLanguages(net.languages ?? "");
      setProfileCustom(net.custom ?? "");
      setProfileSaved(false);
      setProfileError(null);
    }),
  );

  const onSaveProfile = async () => {
    setProfileError(null);
    setProfileSaved(false);
    const net = selectedIdentityNetwork();
    if (!net) return;
    setProfileSaving(true);
    try {
      // Send all 5 fields; a blank one clears it (same "editor owns the
      // full value including clear" contract as identity above).
      await updateProfile(net.slug, {
        age: profileAge(),
        gender: profileGender(),
        location: profileLocation(),
        languages: profileLanguages(),
        custom: profileCustom(),
      });
      setProfileSaved(true);
    } catch (err) {
      setProfileError(
        err instanceof ApiError ? friendlyApiError(err) : "Couldn't save profile. Try again.",
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const onUploadAvatar = async (file: File) => {
    setAvatarError(null);
    const net = selectedIdentityNetwork();
    if (!net) return;
    setAvatarUploading(true);
    try {
      await uploadAvatar(net.slug, file);
    } catch (err) {
      setAvatarError(
        err instanceof ApiError ? friendlyApiError(err) : "Couldn't upload avatar. Try again.",
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const onDeleteAvatar = async () => {
    setAvatarError(null);
    const net = selectedIdentityNetwork();
    if (!net) return;
    setAvatarUploading(true);
    try {
      await deleteAvatar(net.slug);
    } catch (err) {
      setAvatarError(
        err instanceof ApiError ? friendlyApiError(err) : "Couldn't remove avatar. Try again.",
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  // Whether the three identity shadows still say what the server row says.
  // The apply skips an unchanged identity, so this is what decides whether
  // the PATCH (and its reconnect) happens at all.
  const identityDirty = (): boolean => {
    const net = selectedIdentityNetwork();
    if (!net) return false;
    return (
      nickText() !== net.nick ||
      identText() !== (net.ident ?? "") ||
      realnameText() !== (net.realname ?? "")
    );
  };

  // issue 1993 — ONE apply over two endpoints. `PUT /password` and
  // `PATCH /identity` are distinct calls (see the signals above) and BOTH
  // live-apply by bouncing the upstream session, which is what made two
  // buttons the wrong shape: the user's intention is "apply my changes to
  // this network", not "reconnect once per field group".
  //
  // 🔴 It cannot be ONE bounce when BOTH changed, and pretending otherwise
  // would be the lie: each endpoint reconnects server-side, so two writes
  // are two reconnect requests back to back. What this DOES guarantee is
  // that the merge never costs MORE than the two buttons it replaced — each
  // leg is skipped unless it has something to write, so the single-axis
  // cases (the common ones) are exactly one bounce, and an untouched card is
  // zero. A genuinely single bounce needs a combined server verb; that is a
  // server-side scope change and deliberately not in this slice.
  //
  // Password FIRST, and fail-fast on its refusal: a rejected secret (too
  // short, services would refuse it) must not cost a reconnect for the half
  // of the gesture that would have worked, leaving the operator to guess
  // which half landed.
  const onApply = async () => {
    setApplyArmed(false);
    setIdentityError(null);
    setPasswordError(null);
    setApplyResult(null);
    const net = selectedIdentityNetwork();
    if (!net) return;

    let applied = false;

    // Leave-blank-to-keep lives HERE: an empty input is "I did not touch
    // this", never "clear my password". The server 400s a blank precisely so
    // this can never be an accident.
    const pw = passwordText();
    if (pw !== "") {
      setPasswordSaving(true);
      try {
        await updateNetworkPassword(net.slug, pw);
        setPasswordText("");
        applied = true;
      } catch (err) {
        setPasswordError(
          err instanceof ApiError
            ? friendlyApiError(err)
            : "Couldn't save the password. Try again.",
        );
        return;
      } finally {
        setPasswordSaving(false);
      }
    }

    if (identityDirty()) {
      setIdentitySaving(true);
      try {
        // Send all three fields; blank ident/realname clears back to the
        // server default (ident → nick, realname → the subject default). Empty
        // string is a legitimate "unset" intent here — the settings editor is
        // the canonical edit surface, so it owns the full value including
        // clear. Nick is required (the credential can't be nickless).
        await updateIdentity(net.slug, {
          nick: nickText(),
          ident: identText(),
          realname: realnameText(),
        });
        applied = true;
      } catch (err) {
        setIdentityError(
          err instanceof ApiError ? friendlyApiError(err) : "Couldn't apply identity. Try again.",
        );
        return;
      } finally {
        setIdentitySaving(false);
      }
    }

    setApplyResult(applied ? "applied" : "unchanged");
  };

  // The drawer stays mounted across open/close (CSS .open toggle, not a
  // <Show>), so transient armed state would survive a close → reopen.
  // #157: close the delete-account modal so a reopened drawer never strands
  // the irreversible confirm dialog open. (#986 — the quit two-tap latch this
  // effect also disarmed left with the button; the rail's confirm modal is a
  // store-driven singleton that owns its own lifetime.)
  createEffect(() => {
    if (!props.open) {
      setDeleteOpen(false);
      // #152 — disarm the identity apply + clear transient save state so a
      // reopened drawer never sits one tap from a reconnect or shows a
      // stale "applied"/error banner. Reset the seed latch + selected target
      // so the next open re-defaults to the (now-current) focused network and
      // re-seeds the fields from its /networks row.
      setApplyArmed(false);
      setApplyResult(null);
      setIdentityError(null);
      // issue 1993 — the password rides the same apply, so it clears with
      // the rest of the transient state: a secret typed but not applied must
      // not sit in the DOM waiting for the next person to open the drawer.
      setPasswordText("");
      setPasswordError(null);
      setIdentitySeeded(false);
      setSelectedIdentitySlug(null);
      // #282 — clear a stale reconnect error so a reopened drawer that
      // re-enters the vhost sub-page never strands the previous failure.
      setReconnectError(null);
    }
  });

  const refreshDevices = async () => {
    const t = token();
    if (t === null) return;
    try {
      const list = await listPushDevices(t);
      setDevices(list);
    } catch {
      /* swallowed — device list is informational */
    }
  };

  const refreshPrefs = async () => {
    const t = token();
    if (t === null) return;
    try {
      const loaded = await getNotificationPrefs(t);
      // #868 — feed the live notify path the same authoritative map the form
      // renders, so the beep obeys a pref the moment it is read, not on the
      // next user-topic rejoin. Since issue 1480 the form renders THIS, and
      // nothing else: see the note above `savePrefs`.
      mirrorNotificationPrefs(loaded);
      setChannelsOnlyText(loaded.channel_messages_only.join(", "));
      setNicksOnlyText(loaded.private_messages_only.join(", "));
    } catch {
      /* swallowed — fall back to defaults */
    }
  };

  // Load prefs + devices once at mount + probe the SW for an actual
  // PushSubscription. Notification.permission alone is NOT proof of a
  // live subscription on THIS browser profile — the user may have
  // granted permission in another profile / cleared site data /
  // unsubscribed via DevTools. Source of truth: the SW's
  // `pushManager.getSubscription()`. We reflect THAT into pushEnabled.
  onMount(() => {
    void refreshPrefs();
    void refreshDevices();
    void probeLocalSubscription();
    const t = token();
    if (t !== null) {
      // UX-4 bucket M — populate the cic-side upload-TTL cache so the
      // fieldset's `<select>` reflects the server value before the
      // first user interaction.
      void loadUploadTtlSeconds(t);
      void loadUploadConfirmEnabled(t);
      // #348 — same reason: the auto-away control must show what the
      // server stored, not a client-side guess.
      void loadAutoAwayDebounce(t);
      // issue 2150 — same reason again for the two leave-reason inputs.
      // No draft seeding here: the inputs READ THROUGH to the cache until
      // the user types (see `quitPartReasonText`), so this async load
      // surfaces on its own, as does a later push from another device.
      void loadQuitPartReason(t);
      void loadAutoAwayReason(t);
      // M2 — same reason: the peer-profiles toggle must show the
      // subject's actual opt-in, not a client-side guess.
      void loadShowPeerProfiles(t);
      // #228, #251 — load the source-bind (vhost) view so the widget
      // reflects the server's allow-set + current selection.
      void loadVhostSettings(t);
    }
  });

  // UX-6 D12 (2026-05-21) — viewport diagnostics moved to AdminPane
  // Debug tab. The fieldset lived here through the UX-6-D 11-attempt
  // cluster; with the cluster closed and the diag now most useful
  // from a stable admin surface (closing settings to test the
  // keyboard hid the very diag you needed), the readouts + the
  // DiagFloat toggle live in `AdminDebugTab.tsx`. The floating
  // overlay itself (`DiagFloat.tsx`) is unchanged — mounted via
  // Portal in Shell, flag-gated via localStorage.cic_diag.

  // UX-6 bucket A — refcounted overlay scroll-lock. Push on open,
  // pop on close so `<html>` carries `.overlay-open` while any
  // overlay is up. v4: the scroll-lock targets the .settings-drawer
  // aside itself (its own `overflow-y: auto` is the legitimate scroll
  // surface body-scroll-lock-upgrade allows; touchmove on everything
  // else is preventDefaulted). Tracks the parent-owned `props.open`
  // accessor; the prior-value closure ensures one push per open
  // transition and one pop per close transition (no leaks if `open`
  // re-renders with the same value). onCleanup pops on unmount if
  // still open so a route-change mid-open doesn't leave the refcount
  // stuck.
  let drawerEl: HTMLElement | undefined;
  let wasOpen = false;
  createEffect(() => {
    const o = props.open;
    if (o && !wasOpen) {
      wasOpen = true;
      pushOverlay(drawerEl ?? null);
      // #75/#332 — the footer 🎨 launcher requests a deep-link into the
      // themes sub-page before opening; consume it (one-shot). No pending
      // request → stay on "main" (reset on the prior close below).
      const pending = consumePendingSettingsPage();
      if (pending !== null) setSettingsPage(pending);
    } else if (!o && wasOpen) {
      wasOpen = false;
      popOverlay(drawerEl ?? null);
      // #252 — a reopened drawer always lands on the main page.
      setSettingsPage("main");
      // #356 — discard any deep-link request that was set while the drawer
      // was already open (a bump that couldn't re-open it, so its open
      // transition never consumed the pending page). Clearing it on close
      // stops a stranded request from hijacking the NEXT normal open.
      consumePendingSettingsPage();
    }
  });
  onCleanup(() => {
    if (wasOpen) {
      wasOpen = false;
      popOverlay(drawerEl ?? null);
    }
  });

  const probeLocalSubscription = async () => {
    if (typeof navigator === "undefined" || navigator.serviceWorker === undefined) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.pushManager === undefined) return;
      const sub = await registration.pushManager.getSubscription();
      setPushEnabled(sub !== null);
      setCurrentDeviceId(sub === null ? null : subscriptionIdForEndpoint(sub.endpoint));
    } catch {
      /* swallowed — pushEnabled stays false */
    }
  };

  const splitCsv = (s: string): string[] =>
    s
      .split(",")
      .map((x) => x.trim())
      .filter((x) => x !== "");

  // This form has NO private copy of the prefs, and that is the fix issue 1480
  // owes its own CI red. It used to keep one, loaded once at mount, and #950
  // had already patched the single key that grew a second writer
  // (`muted_targets` ← the rail picker) by reading THAT key off the shared
  // mirror. `/beep` made `notification_sound` the second such key and the
  // patch did not generalise: the picker showed `none` to someone who had just
  // typed `/beep chime`, and — worse, because it loses data rather than
  // displaying it late — this endpoint is a FULL replace, so the next
  // unrelated checkbox would have written that stale `none` straight back over
  // the choice.
  //
  // The snapshot was never anything but a duplicate: its only two writers were
  // the two lines below, each already handing the SAME value to the mirror.
  // Deriving costs nothing and cannot drift, and it is what CLAUDE.md's design
  // discipline asks for — the parallel structure WAS the bug, and a third
  // per-key patch would only have deferred the next one.
  const savePrefs = async (next: NotificationPrefs) => {
    const t = token();
    if (t === null) return;
    setSavingPrefs(true);
    setPrefsError(null);
    try {
      const saved = await putNotificationPrefs(t, next);
      // #868 — mirror the server's NORMALIZED echo (not `next`): the whitelists
      // come back folded, which is the form the predicate compares against.
      mirrorNotificationPrefs(saved);
    } catch (err) {
      const code = err instanceof Error ? err.message : "save_failed";
      setPrefsError(code);
    } finally {
      setSavingPrefs(false);
    }
  };

  const togglePref = (key: keyof NotificationPrefs, checked: boolean) => {
    const current = notificationPrefs();
    if (typeof current[key] !== "boolean") return;
    void savePrefs({ ...current, [key]: checked });
  };

  // issue 1480 — the drawer writes the sound the way it writes every checkbox
  // here: merge one key over the mirrored map and PUT the whole thing. The
  // `/beep` verb cannot reach that map — it runs with no drawer mounted at
  // all — so it goes through `notificationPrefs.applyNotificationSound`, which
  // GETs first. Same split as the mute picker (#950), same reason.
  const setNotificationSound = (sound: NotificationSound) => {
    void savePrefs({ ...notificationPrefs(), notification_sound: sound });
  };

  const commitChannelsOnly = () => {
    const next = { ...notificationPrefs(), channel_messages_only: splitCsv(channelsOnlyText()) };
    void savePrefs(next);
  };

  const commitNicksOnly = () => {
    const next = { ...notificationPrefs(), private_messages_only: splitCsv(nicksOnlyText()) };
    void savePrefs(next);
  };

  // #950 — the one key that reached the mirror first, back when the rest of
  // this form still read a private snapshot; issue 1480 moved every other read
  // here too (see `savePrefs`). Kept as its own accessor for the `?? {}`: the
  // key is optional on the wire and three call sites below want a map.
  const mutedTargets = (): MutedTargets => notificationPrefs().muted_targets ?? {};

  // #866 — the per-conversation mute list, sorted by the stored key so the
  // rows do not reshuffle when one is added.
  //
  // #1038 — the key is now the composite ChannelKey, so the row can finally
  // say WHICH network a mute belongs to. It is decoded through the paired
  // `decodeChannelKey` rather than split here: one encoder, one decoder.
  // A key that fails to decode is a pre-#1038 bare leftover the migration
  // could not place (a subject with no credential at the time). It is still
  // SHOWN — with no network — because it is really in the stored map and the
  // operator must be able to remove it; hiding it would leave an entry only
  // the DB knows about.
  const mutedConversations = (): {
    key: ChannelKey;
    network: string | null;
    target: string;
    until: number | null;
  }[] =>
    Object.entries(mutedTargets())
      .map(([raw, target]) => {
        const key = raw as ChannelKey;
        const decoded = decodeChannelKey(key);
        return {
          key,
          network: decoded?.slug ?? null,
          target: decoded?.name ?? key,
          until: target.until,
        };
      })
      .sort((a, b) => a.key.localeCompare(b.key));

  // What the picker offers: the conversations the SIDEBAR shows (joined and
  // parted channels, open queries), never a free-text field over all of IRC —
  // vjt's Q5. Already-muted keys drop out, so the picker cannot re-add a row
  // that is on screen right below it.
  //
  // #1038 — this used to DEDUPE by the folded channel name, so `#grappa` on
  // two networks was offered once, deliberately: the key carried no network
  // and offering it twice would have implied two independent mutes when there
  // was one. That is exactly what vjt reversed. The key now carries the
  // network, so the same channel name on two networks is two rows, and the
  // label names the network to tell them apart. The Map is kept only to
  // collapse a genuine duplicate window, which is now a real duplicate rather
  // than two different conversations colliding.
  const muteCandidates = (): { key: ChannelKey; label: string }[] => {
    const muted = mutedTargets();
    const byKey = new Map<ChannelKey, string>();
    for (const candidate of windowCandidates()) {
      const key = windowMuteKey(candidate);
      if (Object.hasOwn(muted, key) || byKey.has(key)) continue;
      byKey.set(key, `${candidate.channelName} — ${candidate.networkSlug}`);
    }
    return [...byKey]
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.key.localeCompare(b.key));
  };

  // `until: null` — permanent. This picker mutes indefinitely; the TIME-BOXED
  // mute is the rail's picker (#950), which writes an integer `until` through
  // the same shape via `withConversationMute`.
  const muteConversation = (key: ChannelKey) => {
    if (key === "") return;
    void savePrefs({
      ...notificationPrefs(),
      muted_targets: withConversationMute(mutedTargets(), key, null),
    });
  };

  const unmuteConversation = (key: ChannelKey) => {
    void savePrefs({
      ...notificationPrefs(),
      muted_targets: withoutConversationMute(mutedTargets(), key),
    });
  };

  const onMasterToggle = async (checked: boolean) => {
    const t = token();
    if (t === null) return;
    setPushBanner(null);
    if (checked) {
      const result: EnablePushResult = await enablePush(t);
      if (result.status === "enabled") {
        setPushEnabled(true);
        // #964 — the drawer does NOT remount after the toggle, so the mount-time
        // probe never re-runs; take the id straight off the enable result.
        setCurrentDeviceId(result.subscriptionId);
        await refreshDevices();
      } else if (result.status === "permission_denied") {
        setPushEnabled(false);
        setPushBanner(
          "Browser notifications are blocked. Open your browser site settings, allow notifications for this site, then try again.",
        );
      } else if (result.status === "permission_dismissed") {
        setPushEnabled(false);
        setPushBanner("Permission prompt dismissed. Toggle again to re-prompt.");
      } else {
        setPushEnabled(false);
        setPushBanner(
          "Push notifications are not supported in this browser. Install Cicchetto to your home screen for the best experience.",
        );
      }
    } else {
      await disablePush(t);
      setPushEnabled(false);
      setCurrentDeviceId(null);
      await refreshDevices();
    }
  };

  // #964 — opens the row's editor. Seeded with the STORED label, never with
  // the derived default: pre-filling "Firefox on Linux #2" and hitting save
  // would freeze today's ordinal into a stored label, which is the stale
  // number the derived design exists to avoid.
  const startRename = (device: PushDeviceSummary) => {
    setRenameError(null);
    setRenameDraft(device.label ?? "");
    setRenamingId(device.id);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameError(null);
  };

  // Unlike removeDevice, a failure here is NOT swallowed: a 422 past the
  // length cap is the user's own input coming back, and silently dropping
  // it would look like the rename simply did not take.
  const commitRename = async (id: SubscriptionId) => {
    const t = token();
    if (t === null) return;
    try {
      await renamePushDevice(t, id, renameDraft());
      setRenamingId(null);
      setRenameError(null);
      await refreshDevices();
    } catch (err) {
      setRenameError(errorMessage(err));
    }
  };

  const removeDevice = async (id: SubscriptionId) => {
    const t = token();
    if (t === null) return;
    try {
      await deletePushSubscription(t, id);
      await refreshDevices();
    } catch {
      /* swallowed — UI will refresh on next drawer open */
    }
  };

  // #348 — which entry the auto-away `<select>` is showing.
  //
  // A stored value that matches no preset renders as "custom" with the
  // input filled, rather than collapsing onto the nearest rung: the
  // number decides when the bouncer says you are away, so showing a
  // different one would misreport the setting.
  const autoAwaySelectValue = (): string => {
    if (autoAwayCustomMode()) return "custom";
    const seconds = autoAwayDebounceValue();
    if (seconds === null) return "";
    if (seconds === 0) return "off";
    return AUTO_AWAY_PRESETS.some((p) => p.seconds === seconds) ? String(seconds) : "custom";
  };

  const autoAwayCustomValue = (): string => {
    if (autoAwayCustomMode()) return autoAwayCustomDraft();
    const seconds = autoAwayDebounceValue();
    return seconds === null || seconds === 0 ? "" : String(seconds);
  };

  // #348 — persist a new auto-away preference. `null` clears it, `0`
  // switches auto-away off; the accepted range for anything else is the
  // server's to enforce, so a refusal is surfaced verbatim rather than
  // pre-empted by a second copy of the bounds here.
  const saveAutoAway = async (seconds: number | null) => {
    const t = token();
    if (t === null) return;
    setAutoAwaySavingError(null);
    try {
      await saveAutoAwayDebounce(t, seconds);
    } catch (err) {
      setAutoAwaySavingError(err instanceof Error ? err.message : "save_failed");
    }
  };

  // M2 — persist the peer-profiles opt-in. Flipping this ON does not
  // retroactively query anyone already in a joined channel — it only
  // gates the lazy query for nicks seen from here on.
  const onShowPeerProfilesChange = async (e: Event) => {
    const enabled = (e.currentTarget as HTMLInputElement).checked;
    const t = token();
    if (t === null) return;
    setShowPeerProfilesSavingError(null);
    try {
      await saveShowPeerProfiles(t, enabled);
    } catch (err) {
      setShowPeerProfilesSavingError(err instanceof Error ? err.message : "save_failed");
    }
  };

  // Selecting "custom" only opens the input — it is a mode, not a value.
  // Writing here would persist whatever the input was seeded with.
  const onAutoAwayChange = async (e: Event) => {
    const value = (e.currentTarget as HTMLSelectElement).value;

    if (value === "custom") {
      setAutoAwayCustomDraft(autoAwayCustomValue());
      setAutoAwayCustomMode(true);
      return;
    }

    setAutoAwayCustomMode(false);

    if (value === "") return await saveAutoAway(null);
    if (value === "off") return await saveAutoAway(0);
    return await saveAutoAway(Number(value));
  };

  // issue 2150 — what each leave-reason input SHOWS. The draft wins once
  // the user has touched the field (`null` means untouched), otherwise the
  // input reads through to the server-backed cache. That read-through is
  // what makes the async boot load and a push from another device appear
  // without an effect copying values around — and it is also what stops a
  // push from clobbering text someone is mid-way through typing.
  //
  // `??` and not `||`: an empty draft is a real value (the user cleared
  // the field, which is how you delete the setting), so it must NOT fall
  // through to the stored string.
  const quitPartReasonText = (): string => quitPartReasonDraft() ?? quitPartReasonValue() ?? "";
  const autoAwayReasonText = (): string => autoAwayReasonDraft() ?? autoAwayReasonValue() ?? "";

  // Saving posts the field's raw contents — an emptied box included, which
  // is the clear gesture. The server answers with what it stored (`null`
  // for a cleared one), and dropping the draft afterwards snaps the input
  // back to READING THROUGH that answer rather than to a value cic made up.
  const onQuitPartReasonSave = async () => {
    const t = token();
    if (t === null) return;
    setQuitPartReasonSavingError(null);
    try {
      await saveQuitPartReason(t, quitPartReasonText());
      setQuitPartReasonDraft(null);
    } catch (err) {
      setQuitPartReasonSavingError(err instanceof Error ? err.message : "save_failed");
    }
  };

  const onAutoAwayReasonSave = async () => {
    const t = token();
    if (t === null) return;
    setAutoAwayReasonSavingError(null);
    try {
      await saveAutoAwayReason(t, autoAwayReasonText());
      setAutoAwayReasonDraft(null);
    } catch (err) {
      setAutoAwayReasonSavingError(err instanceof Error ? err.message : "save_failed");
    }
  };

  // issue 2181 — the two reason fields commit the way the notification
  // allow-lists next door already did (`commitChannelsOnly` /
  // `commitNicksOnly`): on blur, with Enter as the explicit keyboard commit.
  // The `save` buttons are gone; the two handlers ABOVE are untouched and
  // remain the commit path, so the empty-string-clears-it gesture, the
  // dropped draft and the server round-trip all behave exactly as before —
  // only the trigger moved.
  //
  // The guard asks "is the box different from what is STORED", not "has the
  // user touched it". Two things follow, and both are the reason it is not a
  // `draft() !== null` test. A draft typed and then undone is unchanged text,
  // and a blur must not POST it. And a successful save drops the draft, so
  // the box reads back through to the server's echo — which makes
  // Enter-then-tab-away a SINGLE write rather than two.
  const commitQuitPartReason = () => {
    if (quitPartReasonText() === (quitPartReasonValue() ?? "")) return;
    void onQuitPartReasonSave();
  };

  const commitAutoAwayReason = () => {
    if (autoAwayReasonText() === (autoAwayReasonValue() ?? "")) return;
    void onAutoAwayReasonSave();
  };

  const onAutoAwayCustomSave = async () => {
    // An empty box is not a zero: `Number("")` is 0, which is the OFF
    // sentinel, so committing a blank input would silently switch
    // auto-away off instead of asking for a number.
    const raw = autoAwayCustomDraft().trim();
    const seconds = Number(raw);
    if (raw === "" || !Number.isInteger(seconds)) {
      setAutoAwaySavingError("enter a whole number of seconds");
      return;
    }
    await saveAutoAway(seconds);
  };

  // UX-4 bucket M (2026-05-19) — upload-TTL `<select>` change handler.
  // Reads the host-token from the option `value=`, looks up its `seconds`
  // counterpart from `activeHost().ttlOptions`, and PUTs through.
  // Empty-string value = "use default" sentinel → PUTs `null` to clear
  // the server-side preference.
  const onUploadTtlChange = async (e: Event) => {
    const t = token();
    if (t === null) return;
    const select = e.currentTarget as HTMLSelectElement;
    const v = select.value;
    const next: number | null =
      v === "" ? null : (activeHost().ttlOptions.find((o) => o.value === v)?.seconds ?? null);
    setUploadTtlSavingError(null);
    try {
      await saveUploadTtlSeconds(t, next);
    } catch (err) {
      const code = err instanceof Error ? err.message : "save_failed";
      setUploadTtlSavingError(code);
    }
  };

  // #1883 — the pre-upload confirm opt-in. Write-through like the TTL above:
  // the checkbox reflects the cached mirror, and the PUT updates it so the very
  // next upload honours the change with no reload.
  const onUploadConfirmChange = async (e: Event) => {
    const t = token();
    if (t === null) return;
    const next = (e.currentTarget as HTMLInputElement).checked;
    setUploadConfirmSavingError(null);
    try {
      await saveUploadConfirmEnabled(t, next);
    } catch (err) {
      const code = err instanceof Error ? err.message : "save_failed";
      setUploadConfirmSavingError(code);
    }
  };

  // Current `<select>` value: walk the active host's ladder to find an
  // entry whose `seconds` matches the cached preference. Empty string
  // when the preference is null (renders the "use site default" option).
  const uploadTtlSelectValue = (): string => {
    const seconds = uploadTtlSecondsValue();
    if (seconds === null) return "";
    return activeHost().ttlOptions.find((o) => o.seconds === seconds)?.value ?? "";
  };

  // #206 — human-readable label for the "use site default" option. The
  // default TTL is stored as a raw seconds token ("86400"); resolve it
  // through the SAME ttlOptions ladder the other options render from
  // ("24 hours") instead of leaking the integer. Falls back to the raw
  // token only if the default isn't in the ladder (host misconfig).
  const defaultTtlLabel = (): string => {
    const host = activeHost();
    const d = host.defaultTtl;
    if (d == null) return "";
    return host.ttlOptions.find((o) => o.value === d)?.label ?? d;
  };

  // #228 — load the vhost view. Swallow errors into the error signal (the
  // widget renders only when the view is non-null, so a failed load simply
  // keeps the section hidden — same informational-load posture as
  // refreshDevices, but surfaced inline for diagnostics).
  const loadVhostSettings = async (t: string): Promise<void> => {
    try {
      const view = await getVhostSettings(t);
      setVhostView(view);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "load_failed";
      setVhostError(code);
    }
  };

  // #252 — save-on-change handler for the vhost sub-page. Same PUT flow as
  // the retired #228 `<select multiple>` (clear error → PUT the full
  // selection → update the view → surface a `forbidden_vhost` /
  // `bad_request` code inline); the sub-page reports the new selection up.
  const saveVhostSelection = async (addresses: string[]): Promise<void> => {
    const t = token();
    if (t === null) return;
    setVhostError(null);
    try {
      const view = await putVhostSelection(t, addresses);
      setVhostView(view);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "save_failed";
      setVhostError(code);
    }
  };

  // #282 — explicit "Reconnect to apply". Bounces every connected network
  // via `reconnectConnectedNetworks` (park→reconnect per network — the clean
  // same-account path the home-page Reconnect uses, NOT the #281
  // account-switch client purge) so the new source address binds on the
  // fresh upstream. The `reconnecting` guard blocks double-fire; a failure
  // surfaces inline via friendlyApiError (errors MUST be visible —
  // feedback_silent_retry_anti_pattern).
  const reconnectSession = async (): Promise<void> => {
    if (reconnecting()) return;
    setReconnectError(null);
    setReconnecting(true);
    try {
      await reconnectConnectedNetworks();
    } catch (err) {
      setReconnectError(
        err instanceof ApiError ? friendlyApiError(err) : "reconnect failed (unknown error)",
      );
    } finally {
      setReconnecting(false);
    }
  };

  // #252 — navigate into the vhost sub-page. Re-reads the view on entry so
  // the resolved rDNS names land (the server resolves cold addresses out
  // of band after the drawer-open GET, per the non-blocking cache — a
  // second read on sub-page entry shows the names instead of raw IPs).
  const enterVhostPage = (): void => {
    const t = token();
    if (t !== null) void loadVhostSettings(t);
    // #282 review — clear a stale reconnect error so a back→re-enter within an
    // open drawer never strands a prior failure (close-effect clears it too,
    // but the within-drawer re-entry path is the gap the drawer close misses).
    setReconnectError(null);
    setSettingsPage("vhost");
  };

  // #460 / #476 — the general sub-page is conditionally composed (upload
  // retention is host-gated, per-network identity is network-gated — shown to
  // BOTH subjects whenever a network row exists), so its index subtitle names
  // ONLY the children the current subject will actually see — honest, and
  // consistent with the row's own reactive OR-gate. At least one part is always
  // present (the row is gated on the OR of the two), so it is never empty.
  const generalSubtitle = (): string => {
    const parts: string[] = [];
    if (activeHost().ttlOptions.length > 0) parts.push("upload retention");
    if (hasNetworks()) parts.push("per-network identity");
    return parts.join(" and ");
  };

  // #460 — shared back-header for the INLINE sub-pages (general / profile /
  // display / push), mirroring the AliasSettings / WatchlistsSettings
  // convention so every sub-page presents one consistent "‹ back" affordance.
  // Local (not a component) so it stays inside the signal scope without prop
  // threading.
  //
  // issue 1993 — `backTo` is an explicit parameter, not a default of "main":
  // the profile page is entered from GENERAL, so a hard-coded "main" would
  // drop the user two levels for one tap of back. Passed at every call site
  // (CLAUDE.md: no defaulted arguments — a default here is exactly the silent
  // wrong-target path).
  const subpageHeader = (title: string, backTestId: string, backTo: SettingsSubPage) => (
    <header class="settings-subpage-header">
      <button
        type="button"
        class="settings-back"
        data-testid={backTestId}
        aria-label="back to settings"
        onClick={() => setSettingsPage(backTo)}
      >
        ‹ back
      </button>
      <h3>{title}</h3>
    </header>
  );

  // issue 1993 — the picker that decides WHICH network the cards below are
  // keyed to. Lifted out of the identity card (where #497 left it) so it sits
  // at the top of the network-scoped group instead of inside one of the four
  // things it governs. Shared by the general and profile pages: those two are
  // mutually exclusive `<Show>` blocks, so `id="settings-identity-network"`
  // is still unique in the DOM, and both read the SAME signal — switching on
  // one page is already switched on the other.
  //
  // Still hidden on a single network (#497's ruling, unchanged: a one-option
  // picker is noise, and the group's own heading says the scope).
  const networkScopePicker = () => (
    <Show when={identityNetworks().length > 1}>
      <label for="settings-identity-network">Network</label>
      {/* Lock the target while an apply is in flight — the save captured a
          specific network; switching mid-reconnect would surface its result
          banner under the wrong row. */}
      <select
        id="settings-identity-network"
        data-testid="settings-identity-network-select"
        disabled={identitySaving() || passwordSaving()}
        value={selectedIdentityNetwork()?.slug ?? ""}
        onChange={(e) => setSelectedIdentitySlug(e.currentTarget.value)}
      >
        <For each={identityNetworks()}>{(net) => <option value={net.slug}>{net.slug}</option>}</For>
      </select>
    </Show>
  );

  // issue 1982 — the scrim dismisses on the press it began, not on any click
  // that happens to land on it. A bare watch-family verb reaches
  // `requestOpenSettings` with no `await` ahead of it, Shell's tick effect runs
  // `setSettingsOpen(true)` in the same turn, and `.settings-drawer-backdrop.open`
  // takes `pointer-events: auto` with NO transition — so a tap on send arms this
  // scrim under the finger and the tap's own synthesised click hit-tests onto
  // it, closing the drawer inside the gesture that opened it. Same defect and
  // same cure as issue 1831 on BanlistModal and ModeModal; this is the first
  // site reached through `requestOpenSettings` rather than an `open*Modal`.
  // See lib/backdropDismiss.ts. `props.onClose` is read through an arrow so the
  // dismiss always calls the CURRENT prop, never the one captured at mount.
  const backdropDismiss = createBackdropDismiss(() => props.onClose());

  return (
    <>
      <div
        class="settings-drawer-backdrop"
        classList={{ open: props.open }}
        onPointerDown={backdropDismiss.onPointerDown}
        onClick={backdropDismiss.onClick}
        aria-hidden="true"
        data-testid="settings-drawer-backdrop"
      />
      <aside
        ref={drawerEl}
        class="settings-drawer"
        classList={{ open: props.open }}
        role="dialog"
        aria-label="settings"
      >
        {/* UX-4 bucket L (2026-05-19) — sticky header with × close
            (desktop parity, top-right corner of the drawer). The
            bottom "done" button (added after `log out`) covers the
            mobile thumb-reach case where the × is awkward to tap at
            the top of a tall drawer. */}
        <header class="settings-drawer-header">
          <h2>settings</h2>
          <button
            type="button"
            class="settings-drawer-close"
            aria-label="close settings"
            data-testid="settings-drawer-close"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        {/* #460 — the main settings page is an INDEX of nav rows. Each row
            pushes into a dedicated sub-page (a `<Show>`-gated block that
            replaces the index in place); the header × stays visible for both.
            Below the index sit the subject-gated affordances that are NOT
            sub-pages (the share entry, delete account, done — #986 moved the
            lifecycle verbs to the rail and dropped the duplicate admin
            entry). */}
        <Show when={settingsPage() === "main"}>
          {/* Index rows, in order: general, display, themes, push
              (notifications), watch lists, aliases, on-connect commands,
              source address (LAST). */}

          {/* general — upload retention (host-gated) + per-network identity
              (network-gated, both subjects — #476). The row is gated on the OR
              of its children so it never opens an empty page. */}
          <Show when={activeHost().ttlOptions.length > 0 || hasNetworks()}>
            <button
              type="button"
              class="settings-nav-row"
              data-testid="general-settings-entry"
              onClick={() => setSettingsPage("general")}
            >
              <span class="settings-nav-row-text">
                <span class="settings-nav-row-label">general</span>
                <span class="settings-nav-row-subtitle">{generalSubtitle()}</span>
              </span>
              <span class="settings-nav-row-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          </Show>

          {/* display — text size, timestamp format, colored nicklist (#443). */}
          <Show when={getSubject()?.kind === "user"}>
            <button
              type="button"
              class="settings-nav-row"
              data-testid="security-settings-entry"
              onClick={() => setSettingsPage("security")}
            >
              <span class="settings-nav-row-text">
                <span class="settings-nav-row-label">security</span>
                <span class="settings-nav-row-subtitle">
                  password and two-factor authentication
                </span>
              </span>
              <span class="settings-nav-row-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          </Show>

          {/* display — text size, timestamp format, colored nicklist (#443). */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="display-settings-entry"
            onClick={() => setSettingsPage("display")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">display</span>
              <span class="settings-nav-row-subtitle">text size, timestamps, colored nicklist</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #75 — themes gallery sub-page nav row. Always available (any
              logged-in subject can browse + apply the published + built-in
              gallery). */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="themes-settings-entry"
            onClick={() => setSettingsPage("themes")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">themes</span>
              <span class="settings-nav-row-subtitle">browse and apply color themes</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* push — notifications: push permission + per-trigger prefs +
              device list. */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="push-settings-entry"
            onClick={() => setSettingsPage("push")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">notifications</span>
              <span class="settings-nav-row-subtitle">push permission and per-trigger alerts</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #356 — watch lists sub-page nav row (presence notify + keyword
              highlight, one section). Also deep-linked by the bare
              /notify /watch /hilight /highlight compose verbs via
              requestOpenSettings("watchlists"). */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="watchlists-settings-entry"
            onClick={() => setSettingsPage("watchlists")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">watch lists</span>
              <span class="settings-nav-row-subtitle">presence notify and keyword highlight</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #162 — ignore list sub-page nav row (per-network masks whose
              messages are dropped at delivery). Sits right after watch lists:
              the two are the "who do I want to hear from" pair. */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="ignores-settings-entry"
            onClick={() => setSettingsPage("ignores")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">ignore list</span>
              <span class="settings-nav-row-subtitle">masks whose messages are dropped</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #385 — aliases sub-page nav row (user-defined command aliases).
              Also deep-linked by the bare /alias compose verb via
              requestOpenSettings("aliases"). */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="aliases-settings-entry"
            onClick={() => setSettingsPage("aliases")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">aliases</span>
              <span class="settings-nav-row-subtitle">your own slash-command shortcuts</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #189 — on-connect perform list nav row (per-network raw IRC
              commands run at 001, before autojoin). Sits right after aliases —
              both are command-automation surfaces. */}
          <button
            type="button"
            class="settings-nav-row"
            data-testid="perform-settings-entry"
            onClick={() => setSettingsPage("perform")}
          >
            <span class="settings-nav-row-text">
              <span class="settings-nav-row-label">on-connect commands</span>
              <span class="settings-nav-row-subtitle">raw IRC commands run on connect</span>
            </span>
            <span class="settings-nav-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>

          {/* #252 — source address (vhost) nav row. LAST in the index; the
              interim #228 `<select multiple>` is replaced by a dedicated,
              mobile-friendly SUB-PAGE (tap-select, NAME-primary). It renders
              only once the server view has loaded (non-null). */}
          <Show when={vhostView() !== null}>
            <button
              type="button"
              class="settings-nav-row"
              data-testid="vhost-settings-entry"
              onClick={enterVhostPage}
            >
              <span class="settings-nav-row-text">
                <span class="settings-nav-row-label">source address (vhost)</span>
                <span class="settings-nav-row-subtitle">the address you connect from</span>
              </span>
              <span class="settings-nav-row-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          </Show>

          {/* #986 — the `admin console` entry is GONE. It was an exact
            duplicate of the rail's 🔧 admin action: same `isAdmin()` gate,
            same `setSelectedChannel({ kind: "admin" })` payload, same
            destination. Its `onOpenAdmin` prop and both Shell wirings went
            with it — a dead prop left behind is how the next reader concludes
            the two paths differed. */}

          {/* #392 — session-share entry. #1306 — no longer isVisitor()-gated:
              the mint serves a password subject too, so a user reaches the
              same modal. #363 — still hidden while incognito: an ephemeral
              session must not be portable to another device. #460 — split out
              of the old isVisitor block (identity moved into the general
              sub-page); the share entry STAYS on the main index. */}
          <Show when={canShareSession()}>
            <button
              type="button"
              class="settings-share-button"
              data-testid="share-session-entry"
              onClick={() => openShareModal()}
            >
              <span class="settings-share-button-label">{SHARE_SESSION_LABEL}</span>
              {/* #462 — the subtitle used to BE the label ("open this session
                  on another device") while the label said something else. With
                  the name settled, the second line earns its place by saying
                  what the door actually shows: the modal is a QR code plus a
                  single-use link, not a settings toggle. */}
              <span class="settings-share-button-subtitle muted">
                scan a QR code, or send yourself a single-use link
              </span>
            </button>
          </Show>

          {/* #986 — the canonical session-lifecycle verbs (detach + quit) are
            NO LONGER here. They moved into the rail actions menu
            (RailActions.tsx), each behind the shared #195 confirm modal that
            states, per subject, what the verb actually destroys. Two doors to
            a destructive verb — one confirmed and one not — is worse than
            either alone, so the drawer copies went with the move rather than
            staying as an unconfirmed shortcut.

            `delete account` below STAYS: it is the one irreversible door, it
            keeps its own type-your-name gate, and its geometry is #987. */}

          {/* #157 — delete account: IRREVERSIBLE total wipe, DISTINCT from
            quit (which PRESERVES a persistent identity). It keeps the
            type-your-name modal — #986's ruling is that the typed gate is
            about IRREVERSIBILITY, not subject kind, so it stays here and
            ONLY here. Offered to a registered non-admin user or a registered
            visitor; admins + anon visitors never see it. */}
          <Show when={showDeleteAccount()}>
            {/* #462 — the button and its consequence are ONE affordance, so
                they sit in one box: the drawer's own 1rem flex gap would put
                the sentence as far from its button as from the unrelated
                control above it. The copy comes from lib/lifecycle, beside the
                verb, computed per subject — a user and a registered visitor
                lose different nouns here. */}
            <div class="settings-danger">
              <button
                type="button"
                class="delete-account-entry"
                data-testid="delete-account-btn"
                onClick={() => setDeleteOpen(true)}
              >
                delete account
              </button>
              <p class="settings-section-blurb" data-testid="delete-account-hint">
                {deleteAccountBody()}
              </p>
            </div>
          </Show>

          {/* #1773 — credits. LAST of the drawer's own entries, and outside
            every sub-page on purpose: it is not a setting.

            Deliberately NOT a `.settings-nav-row`, and that is the #460
            contract rather than a styling whim: an index nav row PUSHES a
            sub-page and wears a chevron saying so. This opens a modal, so it
            follows the share entry — the drawer's other modal door — and
            wears its shape instead. Making it a nav row would have put a
            non-page in the index the `renders the index nav rows in order`
            case enumerates.

            The modal itself is mounted in Shell, like the share one:
            `.settings-drawer` animates on `transform`, which makes it the
            containing block for any `position: fixed` descendant, so a
            full-screen modal rendered from here would be clipped to it. */}
          <button
            type="button"
            class="settings-share-button settings-credits-entry"
            data-testid="credits-entry"
            onClick={() => openCreditsModal()}
          >
            <span class="settings-share-button-label">{CREDITS_LABEL}</span>
            <span class="settings-share-button-subtitle muted">
              who built this, and out of what
            </span>
          </button>

          {/* Issue 1974 — running bundle vs deployed bundle. UNGATED on vjt's
            ruling: the person served a stale service-worker cache is an
            ordinary PWA user, so the readout cannot live behind the admin
            gate in `AdminDebugTab`. This is the drawer's only ungated,
            always-reachable surface that is not an easter egg.

            Below `credits` and not among the nav rows, because it is neither
            an entry nor a setting: it PUSHES nothing and it CHANGES nothing.
            The #1773 contract that credits is "LAST of the drawer's own
            entries" is untouched — a readout is not an entry, and the two sit
            together on purpose as the drawer's "about this build" tail. */}
          <BundleReadout />

          {/* UX-4 bucket L — bottom "done" button. Same close verb as
            the top × — mobile thumb-reach surface. Sits below logout
            so the scroll position when scroll-to-bottom lands on a
            thumb-friendly close affordance. */}
          <button
            type="button"
            class="settings-drawer-done"
            data-testid="settings-drawer-done"
            onClick={props.onClose}
          >
            done
          </button>
        </Show>

        <Show when={settingsPage() === "security"}>
          <TotpSettings onBack={() => setSettingsPage("main")} />
        </Show>

        {/* #460 — general sub-page. issue 1993 reshaped it: the NETWORK-scoped
            block (the selector, then the identity + NickServ-password card,
            then the door to the profile page) comes FIRST as one visibly
            grouped unit, and the ACCOUNT-scoped knobs (upload retention,
            auto-away) follow it. Before the rework the two scopes were
            interleaved and the selector governing half of them was buried
            inside one of the four cards it governs. */}
        <Show when={settingsPage() === "general"}>
          <section class="settings-subpage general-subpage" data-testid="general-subpage">
            {subpageHeader("general", "general-back", "main")}

            {/* #476 / #478 — per-network identity editor, both subjects. It
              targets the SELECTED network row (focused-network default), with a
              picker when the subject holds more than one — the retired lowest-id
              anchor is gone. Applying PATCHes /networks/:slug/identity which
              live-applies via internal reconnect (the session bounces +
              rejoins). The confirm-armed apply communicates the reconnect cost;
              a 422 renders inline. Gated on hasNetworks() — you can't edit
              identity for a network you don't hold, and an empty group would
              claim a scope that has no members. */}
            <Show when={hasNetworks()}>
              <section class="settings-network-scope" data-testid="settings-network-scope">
                {/* The heading names the SCOPE, not the network: #497 hides
                    the picker on a single network, so on that (common) path
                    this line is the only thing saying what the cards below
                    are keyed to. */}
                <h4 class="settings-section-heading">per-network</h4>
                {networkScopePicker()}

                {/* #335 — identity sits inside a titled .settings-section card. */}
                <div
                  class="settings-section settings-section-card"
                  data-testid="settings-section-identity"
                >
                  <h4 class="settings-section-heading">identity</h4>
                  <div class="settings-identity" data-testid="settings-identity">
                    <label for="settings-nick">Nick</label>
                    <input
                      id="settings-nick"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={nickText()}
                      onInput={(e) => setNickText(e.currentTarget.value)}
                    />

                    <label for="settings-realname">Real name</label>
                    <input
                      id="settings-realname"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={realnameText()}
                      onInput={(e) => setRealnameText(e.currentTarget.value)}
                    />

                    <label for="settings-ident">Ident</label>
                    <input
                      id="settings-ident"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={identText()}
                      onInput={(e) => setIdentText(e.currentTarget.value)}
                    />

                    {/* #124 — THE one place this secret is editable: it is the
                      credential password, the value `$nickserv_pass` expands
                      to, and for a visitor the credential you log into grappa
                      with. issue 1993 moved it INTO this card and renamed it:
                      "Network password" reads as the server PASS (#1044's
                      distinct secret, which has its own door), and what this
                      writes is what identifies you to NickServ. */}
                    <label for="settings-network-password">NickServ password</label>
                    <input
                      id="settings-network-password"
                      type="password"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      placeholder="leave blank to keep"
                      value={passwordText()}
                      data-testid="settings-network-password-input"
                      onInput={(e) => {
                        setPasswordText(e.currentTarget.value);
                        setApplyResult(null);
                      }}
                    />
                    <p class="settings-identity-hint">
                      A blank password keeps the stored one, and applying reconnects your session.
                    </p>

                    <InlineConfirmButton
                      idleLabel={identitySaving() || passwordSaving() ? "applying…" : "apply"}
                      confirmLabel="apply — this reconnects"
                      testId="settings-identity-apply"
                      armed={applyArmed()}
                      onArm={() => setApplyArmed(true)}
                      onConfirm={() => {
                        void onApply();
                      }}
                    />

                    {/* Two calls, two failures: which door refused is the
                        first thing the operator needs, so the banners stay
                        separate even though the gesture merged. */}
                    <Show when={passwordError()}>
                      {(msg) => (
                        <p
                          role="alert"
                          class="settings-identity-error"
                          data-testid="settings-password-error"
                        >
                          {msg()}
                        </p>
                      )}
                    </Show>
                    <Show when={identityError()}>
                      {(msg) => (
                        <p
                          role="alert"
                          class="settings-identity-error"
                          data-testid="settings-identity-error"
                        >
                          {msg()}
                        </p>
                      )}
                    </Show>
                    <Show when={applyResult()}>
                      {(result) => (
                        <p class="settings-identity-ok" data-testid="settings-identity-ok">
                          {result() === "applied" ? "Applied." : "No changes to apply."}
                        </p>
                      )}
                    </Show>
                  </div>
                </div>
              </section>
            </Show>

            {/* issue 1993 — profile, avatar and the peer-profiles opt-in are
                many fields for something set once, so they moved behind this
                row into their own sub-page (the drawer's existing sub-page
                mechanism, not a new disclosure idiom). UNGATED on networks,
                and OUTSIDE the group above, because the page it opens also
                carries the ACCOUNT-scoped opt-in: gating the door on a
                network would strand that control for a subject holding
                none. */}
            <button
              type="button"
              class="settings-nav-row"
              data-testid="profile-settings-entry"
              onClick={() => setSettingsPage("profile")}
            >
              <span class="settings-nav-row-text">
                <span class="settings-nav-row-label">profile</span>
                <span class="settings-nav-row-subtitle">
                  your CTCP USERINFO fields and avatar, and whether to ask other people for theirs
                </span>
              </span>
              <span class="settings-nav-row-chevron" aria-hidden="true">
                ›
              </span>
            </button>

            {/* #497 — upload retention moved BELOW identity: identity is what a
              user looks for; upload duration is a rarely-touched knob.
              UX-4 bucket M (2026-05-19) — host-gated: only renders when the
              active image host exposes ttlOptions (litterbox does; a
              hypothetical imgur-style host wouldn't). The `<option value="">`
              "use site default" entry maps to a `null` PUT — clears the
              preference and falls back to `activeHost().defaultTtl`. Server
              stores integer seconds, cic translates at this boundary. */}
            <Show when={activeHost().ttlOptions.length > 0}>
              <fieldset class="upload-ttl-fieldset">
                <legend>upload retention</legend>
                {/* #1227 — the visible "upload duration:" text was a second
                    name for the control the legend already names, and on a
                    phone it ate the width the select needed (the option text
                    was cut off mid-word). The <label> stays as the row's flex
                    box; the accessible name moves onto the select itself,
                    because a <legend> names the GROUP and would leave the
                    control nameless for a screen reader. */}
                <label>
                  <select
                    aria-label="upload duration"
                    data-testid="upload-ttl-select"
                    value={uploadTtlSelectValue()}
                    onChange={(e) => {
                      void onUploadTtlChange(e);
                    }}
                  >
                    <option value="">use site default ({defaultTtlLabel()})</option>
                    <For each={activeHost().ttlOptions}>
                      {(opt) => <option value={opt.value}>{opt.label}</option>}
                    </For>
                  </select>
                </label>
                {/* #462 — the select alone answers none of the three questions
                    it raises, and the third is load-bearing: the TTL is read at
                    UPLOAD time (uploadOrchestrator picks the host token when it
                    posts the file), so this is not a retention setting over a
                    library — it applies to the next upload and cannot reach
                    back to the last one. issue 1993 cut the paragraph to one
                    line; all three facts survive the cut, which is the point —
                    the copy pass was about LENGTH, not about dropping what
                    #462 measured a user cannot guess. */}
                <p class="settings-section-blurb" data-testid="upload-ttl-hint">
                  Your preference over the site default: the host deletes each new upload when its
                  time is up, and files you have already uploaded keep the duration they were sent
                  with.
                </p>
                <Show when={uploadTtlSavingError() !== null}>
                  <p class="upload-ttl-error" role="alert" data-testid="upload-ttl-error">
                    {uploadTtlSavingError()}
                  </p>
                </Show>
                {/* #1883 — the pre-upload confirm opt-in, in this fieldset
                    because it is the other thing an operator decides ABOUT an
                    upload before making one, and they are looked for together.
                    NOT host-gated on ttlOptions in its own right — the confirm
                    is a cic dialog and applies to any host — but it inherits
                    this fieldset's gate, which is a real limitation: a host
                    without a TTL ladder would hide the toggle too. Acceptable
                    while litterbox is the only host; if a second one lands,
                    this pair splits into its own fieldset. */}
                <label class="upload-confirm-row">
                  <input
                    type="checkbox"
                    data-testid="upload-confirm-toggle"
                    checked={uploadConfirmEnabledValue()}
                    onChange={(e) => {
                      void onUploadConfirmChange(e);
                    }}
                  />
                  Ask before sending a file
                </label>
                {/* Says what it costs as well as what it buys: the whole reason
                    this is a setting is that the confirm was friction for the
                    operators who did not want it. */}
                <p class="settings-section-blurb" data-testid="upload-confirm-hint">
                  Off by default; when on, every file you pick, drop, paste or share to Grappa waits
                  for you to confirm before it is uploaded and its link posted.
                </p>
                <Show when={uploadConfirmSavingError() !== null}>
                  <p class="upload-ttl-error" role="alert" data-testid="upload-confirm-error">
                    {uploadConfirmSavingError()}
                  </p>
                </Show>
              </fieldset>
            </Show>

            {/* issue 2157 — the client-side video transcode, on this device.
                OUTSIDE the host-gated Show above, and in a fieldset of its
                own rather than joining the upload pair: the transcode is a
                property of this BROWSER (WebCodecs, its CPU, its battery),
                so a host with no TTL ladder must not hide it — and the
                toggle it would have sat beside is account-synced, which
                #2029 named as the thing not to do ("two adjacent checkboxes
                that persist differently is a promise the interface should
                not break"). Device-local for the same reason the switch
                exists: iOS re-encodes on export, which is not true of the
                desktop browser where the transcode is the only thing that
                shrinks a 200MB capture to something the cap accepts. */}
            <fieldset class="video-processing-fieldset">
              <legend>video processing</legend>
              <label>
                <input
                  type="checkbox"
                  data-testid="video-processing-toggle"
                  checked={videoProcessing()}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setVideoProcessingEnabled(on);
                    setVideoProcessing(on);
                  }}
                />
                {VIDEO_PROCESSING_LABEL}
              </label>
              {/* Says what it COSTS, not just what it does. Turning it off
                  does not make big clips upload — it makes them get refused,
                  and that is the sentence an operator needs before flipping
                  it, not after. The length limit is named too because it is
                  POLICY and survives either way: it is the one rejection the
                  switch cannot buy you out of. ONE sentence, one full stop —
                  issue 1993 pins the shape of every blurb on this page, and
                  four facts in one line is what that costs. */}
              <p class="settings-section-blurb" data-testid="video-processing-hint">
                On this device only: Grappa shrinks a clip to fit the upload limit before sending
                it, iPhones already re-encode on export, and with this off an over-limit clip is
                refused instead of shrunk while the length limit still applies.
              </p>
            </fieldset>

            {/* #348 — how long after your last device goes away the bouncer
                tells the network you are away. The delay and the off switch
                are ONE control (vjt's ruling): they answer one question, and
                two widgets could contradict each other. Outside the
                host-gated Show above — this has nothing to do with uploads —
                and shown to visitors too, whose sessions auto-away like
                everyone else's. */}
            <fieldset class="auto-away-fieldset">
              <legend>auto-away</legend>
              {/* #1766 — the visible "mark me away after:" text is GONE (vjt:
                  "e' gia' incluso nel titolo del fieldset"). It was a second
                  name for the control the legend already names, with the blurb
                  below spelling the behaviour out in full underneath it.

                  The catch, and why this is not a deletion: that <label>
                  WRAPPED the <select>, so the text WAS the select's accessible
                  name — dropping the string alone leaves the control nameless.
                  Same cure #1227 applied to the upload-duration select two
                  fieldsets up: the <label> stays as the row's flex box, the
                  name moves onto the control. NOT the <legend> instead — a
                  legend names the GROUP, and a screen reader landing on the
                  select would still be told nothing.

                  issue 1993 (5) — that leftover <label> is a flex ROW, so the
                  select inside it was sized by its own option text. The width
                  rule lives in the sheet, on the class both bare-label
                  fieldsets share; the markup here is unchanged. */}
              <label>
                <select
                  aria-label="auto-away delay"
                  data-testid="auto-away-select"
                  value={autoAwaySelectValue()}
                  onChange={(e) => {
                    void onAutoAwayChange(e);
                  }}
                >
                  <option value="">use site default</option>
                  <option value="off">never — stay online</option>
                  <For each={AUTO_AWAY_PRESETS}>
                    {(preset) => <option value={String(preset.seconds)}>{preset.label}</option>}
                  </For>
                  <option value="custom">custom…</option>
                </select>
              </label>
              <Show when={autoAwaySelectValue() === "custom"}>
                <label>
                  seconds:
                  <input
                    type="number"
                    inputmode="numeric"
                    data-testid="auto-away-custom-input"
                    value={autoAwayCustomValue()}
                    onInput={(e) => setAutoAwayCustomDraft(e.currentTarget.value)}
                  />
                  <button
                    type="button"
                    data-testid="auto-away-custom-save"
                    onClick={() => {
                      void onAutoAwayCustomSave();
                    }}
                  >
                    save
                  </button>
                </label>
              </Show>
              <p class="settings-section-blurb" data-testid="auto-away-hint">
                How long after your last device closes before the bouncer tells the network you are
                away — it stays connected either way, and "never" keeps the flag off entirely.
              </p>
              <Show when={autoAwaySavingError() !== null}>
                <p class="auto-away-error" role="alert" data-testid="auto-away-error">
                  {autoAwaySavingError()}
                </p>
              </Show>

              {/* issue 2150 — the TEXT that delay eventually sends. It sits
                  INSIDE the auto-away fieldset rather than in one of its
                  own: it is meaningless without the delay above it, and
                  "never — stay online" makes it inert. One question, one
                  group. Empty means the bouncer keeps its own wording,
                  which is deliberately not printed here as a placeholder —
                  that string is server-owned and a copy would drift. */}
              {/* issue 2181 — `reason:` used to be a bare text node sharing
                  the flex row with the input and its save button. A bare text
                  node is an ANONYMOUS flex item: it lays out, but no selector
                  can reach it, so the label could not be moved anywhere. The
                  <span> is what gives it an identity; it stays INSIDE the
                  <label>, so the implicit association is untouched and the
                  input keeps its accessible name. */}
              <label class="leave-reason-row">
                <span class="leave-reason-label">reason:</span>
                <input
                  type="text"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck={false}
                  placeholder="leave empty for the default"
                  data-testid="auto-away-reason-input"
                  value={autoAwayReasonText()}
                  onInput={(e) => setAutoAwayReasonDraft(e.currentTarget.value)}
                  onBlur={commitAutoAwayReason}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitAutoAwayReason();
                    }
                  }}
                />
              </label>
              <Show when={autoAwayReasonSavingError() !== null}>
                <p class="auto-away-error" role="alert" data-testid="auto-away-reason-error">
                  {autoAwayReasonSavingError()}
                </p>
              </Show>
            </fieldset>

            {/* issue 2150 — the message sent when YOU leave, as opposed to
                the one above that the bouncer sends for you. Account-wide
                and not per-network: a second copy per network buys nothing
                the account-wide one does not cover, and would drag in the
                selector and the "which network is this keyed to" heading
                (vjt's scope ruling on the issue).

                Outside the auto-away fieldset because it answers a
                different question, and outside the host-gated upload block
                because it has nothing to do with uploads. */}
            <fieldset class="leave-reason-fieldset">
              <legend>leave message</legend>
              <label class="leave-reason-row">
                <input
                  type="text"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck={false}
                  placeholder="leave empty for none"
                  aria-label="quit and part message"
                  data-testid="quit-part-reason-input"
                  value={quitPartReasonText()}
                  onInput={(e) => setQuitPartReasonDraft(e.currentTarget.value)}
                  onBlur={commitQuitPartReason}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitQuitPartReason();
                    }
                  }}
                />
              </label>
              {/* ONE sentence, and not by taste: a general-subpage test
                  counts full stops across every `.settings-section-blurb`
                  and fails a second one. */}
              <p class="settings-section-blurb" data-testid="quit-part-reason-hint">
                Sent when you leave a channel or disconnect without typing your own message — one
                typed on the spot still wins, and an empty box sends none.
              </p>
              <Show when={quitPartReasonSavingError() !== null}>
                <p class="auto-away-error" role="alert" data-testid="quit-part-reason-error">
                  {quitPartReasonSavingError()}
                </p>
              </Show>
            </fieldset>
          </section>
        </Show>

        {/* issue 1993 — profile sub-page, entered from the general page (its
            back button returns THERE, not to the index). Two scopes on one
            page, kept visibly apart: the CTCP USERINFO fields + the avatar are
            per-network and sit under the same picker the identity card uses,
            while the peer-profiles opt-in is account-wide and sits outside the
            group. The issue floats making that opt-in network-scoped too;
            that is a server-side change and deliberately NOT in this slice, so
            the layout states today's truth rather than anticipating it. */}
        <Show when={settingsPage() === "profile"}>
          <section class="settings-subpage profile-subpage" data-testid="profile-subpage">
            {subpageHeader("profile", "profile-back", "general")}

            <Show when={hasNetworks()}>
              <section class="settings-network-scope" data-testid="settings-network-scope">
                <h4 class="settings-section-heading">per-network</h4>
                {networkScopePicker()}

                {/* KVIrc-style CTCP USERINFO profile (age/gender/location/
                  languages/a free custom field), per network — targets the
                  same selected network the identity card does. Unlike
                  identity, saving does NOT reconnect: these fields never ride
                  the IRC handshake, they only feed the server's CTCP
                  USERINFO auto-reply — so a plain save, no two-tap confirm,
                  and no reason to share the identity card's apply. */}
                <div
                  class="settings-section settings-section-card"
                  data-testid="settings-section-profile"
                >
                  <h4 class="settings-section-heading">profile</h4>
                  <div class="settings-identity" data-testid="settings-profile">
                    <label for="settings-profile-age">Age</label>
                    <input
                      id="settings-profile-age"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={profileAge()}
                      onInput={(e) => setProfileAge(e.currentTarget.value)}
                    />

                    <label for="settings-profile-gender">Gender</label>
                    <select
                      id="settings-profile-gender"
                      data-testid="settings-profile-gender"
                      value={profileGender()}
                      onChange={(e) => setProfileGender(e.currentTarget.value)}
                    >
                      <option value="">unset</option>
                      <option value="male">male</option>
                      <option value="female">female</option>
                      <option value="nonbinary">non-binary</option>
                    </select>

                    <label for="settings-profile-location">Location</label>
                    <input
                      id="settings-profile-location"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={profileLocation()}
                      onInput={(e) => setProfileLocation(e.currentTarget.value)}
                    />

                    <label for="settings-profile-languages">Languages</label>
                    <input
                      id="settings-profile-languages"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={profileLanguages()}
                      onInput={(e) => setProfileLanguages(e.currentTarget.value)}
                    />

                    <label for="settings-profile-custom">Custom</label>
                    <input
                      id="settings-profile-custom"
                      type="text"
                      autocapitalize="none"
                      autocorrect="off"
                      spellcheck={false}
                      value={profileCustom()}
                      onInput={(e) => setProfileCustom(e.currentTarget.value)}
                    />
                    <p class="settings-identity-hint">
                      Answered to a CTCP USERINFO query, and a blank field clears it.
                    </p>

                    <button
                      type="button"
                      class="settings-identity-apply"
                      data-testid="settings-profile-apply"
                      disabled={profileSaving()}
                      onClick={() => void onSaveProfile()}
                    >
                      {profileSaving() ? "saving…" : "save profile"}
                    </button>

                    <Show when={profileError()}>
                      {(msg) => (
                        <p
                          role="alert"
                          class="settings-identity-error"
                          data-testid="settings-profile-error"
                        >
                          {msg()}
                        </p>
                      )}
                    </Show>
                    <Show when={profileSaved()}>
                      <p class="settings-identity-ok" data-testid="settings-profile-ok">
                        Profile saved.
                      </p>
                    </Show>
                  </div>
                </div>

                {/* M3a — the own avatar, per network. A permanent, self-hosted
                  upload (same `Grappa.Uploads` pipeline as any other embedded
                  upload, just `expires_at: nil`), served over CTCP AVATAR to
                  whoever asks and rendered in peers' WHOIS cards. Never
                  bounces the connection, like /profile above. */}
                <div
                  class="settings-section settings-section-card"
                  data-testid="settings-section-avatar"
                >
                  <h4 class="settings-section-heading">avatar</h4>
                  <div class="settings-identity" data-testid="settings-avatar">
                    <Show when={selectedIdentityNetwork()?.avatar_url}>
                      {(url) => (
                        <img
                          src={url()}
                          alt="Current avatar"
                          class="settings-avatar-preview"
                          width={64}
                          height={64}
                        />
                      )}
                    </Show>

                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      data-testid="settings-avatar-file"
                      disabled={avatarUploading()}
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0];
                        e.currentTarget.value = "";
                        if (file) void onUploadAvatar(file);
                      }}
                    />
                    <p class="settings-identity-hint">Answered to a CTCP AVATAR query.</p>

                    <Show when={selectedIdentityNetwork()?.avatar_url}>
                      <button
                        type="button"
                        class="settings-identity-apply"
                        data-testid="settings-avatar-remove"
                        disabled={avatarUploading()}
                        onClick={() => void onDeleteAvatar()}
                      >
                        remove avatar
                      </button>
                    </Show>

                    <Show when={avatarUploading()}>
                      <p class="settings-identity-hint" data-testid="settings-avatar-uploading">
                        uploading…
                      </p>
                    </Show>
                    <Show when={avatarError()}>
                      {(msg) => (
                        <p
                          role="alert"
                          class="settings-identity-error"
                          data-testid="settings-avatar-error"
                        >
                          {msg()}
                        </p>
                      )}
                    </Show>
                  </div>
                </div>
              </section>
            </Show>

            {/* M2 — opt-in to grappa querying other people's CTCP USERINFO
                profile (the member-list gender badge's source). Off by
                default: nobody gets an outbound CTCP query from this
                bouncer just for existing in a shared channel. ACCOUNT-wide,
                so it sits outside the per-network group above. */}
            <fieldset class="show-peer-profiles-fieldset">
              <legend>peer profiles</legend>
              <label>
                <input
                  type="checkbox"
                  checked={showPeerProfilesValue()}
                  onChange={(e) => {
                    void onShowPeerProfilesChange(e);
                  }}
                  data-testid="show-peer-profiles-toggle"
                />
                show other people's profile info (gender badge)
              </label>
              <p class="settings-section-blurb" data-testid="show-peer-profiles-hint">
                Off by default; when on, grappa asks each new person's client for their public
                profile and shows a gender badge when they answer.
              </p>
              <Show when={showPeerProfilesSavingError() !== null}>
                <p
                  class="show-peer-profiles-error"
                  role="alert"
                  data-testid="show-peer-profiles-error"
                >
                  {showPeerProfilesSavingError()}
                </p>
              </Show>
            </fieldset>
          </section>
        </Show>

        {/* #460 — display sub-page: text size, timestamp format, and the
            colored-nicklist toggle (#443 section moved VERBATIM). */}
        <Show when={settingsPage() === "display"}>
          <section class="settings-subpage display-subpage" data-testid="display-subpage">
            {subpageHeader("display", "display-back", "main")}

            {/* #299 — the legacy auto/mirc-light/irssi-dark radio selector was
                removed. It is superseded by the #75 theme gallery (a themes
                nav row on the index) and was broken: an active gallery theme
                layers inline CSS vars over the [data-theme] base blocks, so
                toggling the radio did nothing visible. The base look is
                OS-resolved at boot (lib/theme.applyTheme). */}
            <section class="settings-section" data-testid="settings-section-display">
              <h4 class="settings-section-heading">display options</h4>

              {/* issue 2164 — six rungs now (XS at the ruled 11px), driven off
                  lib/fontSize's ladder rather than written out one by one: the
                  px belongs to the module that writes the CSS var, and a copy
                  here is a second place for it to drift. The rungs stay RADIOS
                  and keep their `font-size-<KEY>` testids verbatim — four e2e
                  specs address them. The advanced box beside them is the
                  escape hatch; nothing is checked while it holds a size no
                  rung offers, which is the honest render of that state. */}
              <fieldset class="font-size-fieldset">
                <legend>text size</legend>
                <For each={FONT_SIZE_PRESETS}>
                  {(preset) => (
                    <label>
                      <input
                        type="radio"
                        name="font-size"
                        value={String(preset.px)}
                        checked={fontPx() === preset.px}
                        onChange={onFontSizePresetChange}
                        data-testid={`font-size-${preset.key}`}
                      />
                      {preset.key}
                    </label>
                  )}
                </For>
                <label>
                  px:
                  <input
                    type="number"
                    inputmode="numeric"
                    min={FONT_SIZE_MIN_PX}
                    max={FONT_SIZE_MAX_PX}
                    step="1"
                    aria-label="custom text size in pixels"
                    data-testid="font-size-custom-input"
                    value={fontPx()}
                    onChange={onFontSizeCustomChange}
                  />
                </label>
              </fieldset>

              {/* #217 — message timestamp format. Closed-set (with/without
              seconds), client-only, persisted in localStorage. Mirrors the
              text-size radio-group pattern. */}
              <fieldset class="time-format-fieldset">
                <legend>timestamp format</legend>
                <label>
                  <input
                    type="radio"
                    name="time-format"
                    value="hms"
                    checked={timeFmt() === "hms"}
                    onChange={onTimeFormatChange}
                    data-testid="time-format-hms"
                  />
                  with seconds (HH:MM:SS)
                </label>
                <label>
                  <input
                    type="radio"
                    name="time-format"
                    value="hm"
                    checked={timeFmt() === "hm"}
                    onChange={onTimeFormatChange}
                    data-testid="time-format-hm"
                  />
                  no seconds (HH:MM)
                </label>
              </fieldset>

              {/* #1766 — ONE fieldset for the three checkboxes (vjt: "andiamo
                ad accorpare in un unico fieldset i checkbox esistenti e quello
                nuovo"). They were three one-row fieldsets carrying three
                legends — `nicklist`, `jump button`, and a third on the way —
                which is three boxes to say three sentences. The two RADIO
                groups above keep theirs: a radio group is a fieldset by
                nature, because the box is what makes the exclusivity legible.

                The dissolved classes (`.colored-nicklist-fieldset`,
                `.hide-next-active-fieldset`) are referenced NOWHERE else —
                measured, no CSS rule and no selector in the e2e tree — which
                is what makes the churn free. The `data-testid`s are the real
                contract and stay VERBATIM; the e2e suite addresses them. */}
              <fieldset class="display-checkboxes-fieldset">
                <legend>options</legend>

                {/* #443 — per-nick colors in the members pane. Off by default:
                  the nicklist stays monochrome so its color reads as the mode
                  tier, not identity. When on, MembersPane drops `noColor` so
                  NickText applies the per-nick hash hue; the mode-prefix glyph
                  keeps its own tier color either way. */}
                <label>
                  <input
                    type="checkbox"
                    checked={coloredNicklist()}
                    onChange={onColoredNicklistChange}
                    data-testid="colored-nicklist-toggle"
                  />
                  show colored nicklist
                </label>

                {/* #2029 — strip mIRC formatting on render. OFF by default:
                  colours keep working as they do today and this is the opt-in.
                  Requested by `morph` (Azzurra staff) after a channel filled
                  with heavily coloured bot output. Deliberately NOT channel
                  mode `+c`, which is an operator's channel-wide policy and
                  REJECTS the message — costing the reader the words as well as
                  the colours. This strips on RENDER only: the raw line is
                  untouched on the wire and in scrollback, so turning it back
                  off restores the colours with no reconnect. Sits next to the
                  nicklist row because both are colour-rendering prefs. */}
                <label>
                  <input
                    type="checkbox"
                    checked={getStripFormatting()}
                    onChange={onStripFormattingChange}
                    data-testid="strip-formatting-toggle"
                  />
                  strip colours and formatting from messages
                </label>

                {/* issue 2167 — bold on own-nick mention rows, ON by default:
                  an opt-OUT, never a default change. The bold is genuinely
                  contested (one reader called it annoying, another
                  "comodissimo" in the same channel), which is why it is a
                  preference rather than a flipped default. It is also the
                  first cosmetic knob landed under vjt's option-A ruling: the
                  ask was a free-form custom CSS block, and the ruling took
                  scoped preferences instead — "così sono accessibili a tutti".
                  ⚠️ The bold carries weight: `.scrollback-highlight` (watchlist
                  match) is deliberately not bold, so turning this off must not
                  make the two states identical. It does not — the mention row
                  keeps its `--mention` background and the highlight keeps its
                  accent bar. Sits next to the two rows above because all three
                  are scrollback-rendering prefs. */}
                <label>
                  <input
                    type="checkbox"
                    checked={getBoldMentions()}
                    onChange={onBoldMentionsChange}
                    data-testid="bold-mentions-toggle"
                  />
                  bold the rows that mention your nick
                </label>

                {/* #2037 B — the events badge, OFF by default. vjt's ruling:
                  "i !messaggi non sono interessanti e devono solo finire
                  nell'opt-in badge". The sidebar's faint pill counted
                  everything that is not a message, next to a bold pill that
                  counted messages and a far-behind bar that counted raw rows —
                  three numbers for what a reader takes to be one quantity
                  (#2037). The bar and the bold pill are now the same number by
                  construction; this takes the third out of the default view.
                  ⚠️ Wider than join/part: the bucket is every non-content kind,
                  so `topic`, `kick` and `server_event` follow it — they sit
                  outside the suppressed-presence set on purpose (#458) because
                  the PANE still renders them, which is a different question
                  from whether they earn a badge. A kick that must stay loud
                  belongs in the mention channel (#267). */}
                <label>
                  <input
                    type="checkbox"
                    checked={getShowEventBadge()}
                    onChange={onShowEventBadgeChange}
                    data-testid="show-event-badge-toggle"
                  />
                  count joins, parts and other events in the sidebar badge
                </label>

                {/* #1766 — the mobile window bar (BottomBar), ON by default:
                  an opt-OUT, never a default change. #174's ruling stands
                  ("the bottom bar must NOT be deleted, it stays opt-in from
                  settings") and #71's second ruling reversed "kill the mobile
                  bottom bar" outright. The bar is O(windows), not O(screens),
                  so at 7 networks the strip is longer than the useful scroll
                  distance and the picker stops picking. Turning it off ships a
                  left ☰ with it, so the #1041 edge swipe is not left as the
                  whole navigation surface. */}
                <label>
                  <input
                    type="checkbox"
                    checked={getShowBottomBar()}
                    onChange={onShowBottomBarChange}
                    data-testid="show-bottom-bar-toggle"
                  />
                  show the window bar on mobile
                </label>

                {/* #914 — hide the #235 "jump to next active window" (»N)
                  button. Off by default. PRESENTATIONAL: it removes the button
                  in BOTH placements (desktop sidebar + mobile overlay) and
                  nothing else — Alt+A and Ctrl+N keep jumping. */}
                <label>
                  <input
                    type="checkbox"
                    checked={getHideNextActive()}
                    onChange={onHideNextActiveChange}
                    data-testid="hide-next-active-toggle"
                  />
                  hide the jump-to-next-active button
                </label>

                {/* The one thing the merge HIDES, said out loud rather than
                  inherited: these rows do not persist alike. All but one are
                  #449 server-backed and converge across every device on the
                  account; the jump button is per-DEVICE localStorage, and
                  deliberately so — #914's complaint was about a viewport, not
                  an account. Under one legend, identical-looking rows would
                  otherwise behave differently on a second device with nothing
                  on screen to say why. Phrased by BEHAVIOUR rather than by
                  position ("the first two" until #2029 added a third synced
                  row): a blurb that counts its neighbours is wrong the next
                  time one arrives, silently, because nothing type-checks a
                  sentence. */}
                <p class="settings-section-blurb" data-testid="display-checkboxes-hint">
                  Only the jump button is remembered on this device alone — the rest follow your
                  account onto every device you use.
                </p>
              </fieldset>
            </section>
          </section>
        </Show>

        {/* #460 — push sub-page: notifications (push permission + per-trigger
            prefs + device list). Moved VERBATIM from the old flat main page. */}
        <Show when={settingsPage() === "push"}>
          <section class="settings-subpage push-subpage" data-testid="push-subpage">
            {subpageHeader("notifications", "push-back", "main")}

            <fieldset class="notifications-fieldset">
              <legend>notifications</legend>
              <label class="master-toggle">
                <input
                  type="checkbox"
                  checked={pushEnabled()}
                  // #459 — discover unavailability UP-FRONT (same
                  // `pushAvailable()` gate as the login opt-in banner) instead
                  // of only after the click surfaces enablePush's `unsupported`
                  // arm. A disabled toggle over a dead capability reads as
                  // honest; the hint below says why.
                  disabled={!pushAvailable()}
                  onChange={(e) => {
                    void onMasterToggle((e.currentTarget as HTMLInputElement).checked);
                  }}
                  data-testid="push-master-toggle"
                />
                enable browser notifications
              </label>
              <Show when={!pushAvailable()}>
                <p class="push-banner" role="status" data-testid="push-unavailable">
                  Push notifications aren't available in this browser. On iOS, install Cicchetto to
                  your home screen first.
                </p>
              </Show>
              <Show when={pushBanner() !== null}>
                <p class="push-banner" role="alert" data-testid="push-banner">
                  {pushBanner()}
                </p>
              </Show>

              <hr />

              {/* #1480 — the in-app beep preset. FIRST in the section, above
                  the trigger checkboxes, because "make it stop making that
                  noise" is what sends people here and it is the one control
                  that answers it; the checkboxes answer "when", which is the
                  question you ask second. Default is `silent`, so for most
                  subjects this select is the opt-IN. */}
              <h3>sound</h3>
              <p class="prefs-hint">
                Played in the app when a conversation you are not looking at notifies you. Silent by
                default — the browser notification is a separate switch above.
              </p>
              <label class="prefs-list">
                notification sound:
                <select
                  value={notificationPrefs().notification_sound ?? DEFAULT_NOTIFICATION_SOUND}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    setNotificationSound(
                      (e.currentTarget as HTMLSelectElement).value as NotificationSound,
                    )
                  }
                  data-testid="pref-notification-sound"
                >
                  <For each={NOTIFICATION_SOUNDS}>
                    {(name) => (
                      <option value={name}>{NOTIFICATION_SOUND_PRESETS[name].label}</option>
                    )}
                  </For>
                </select>
              </label>
              {/* Not decoration. Browsers keep the AudioContext suspended until
                  a user gesture, and the live notify path has no gesture of its
                  own — it leans on "something was clicked earlier this session".
                  A deliberate tap here IS that gesture, made on the very surface
                  where the choice is being made, so the first real notification
                  after choosing is audible instead of swallowed. */}
              <button
                type="button"
                class="prefs-preview"
                onClick={() =>
                  playBeep(notificationPrefs().notification_sound ?? DEFAULT_NOTIFICATION_SOUND)
                }
                data-testid="pref-notification-sound-preview"
              >
                preview
              </button>

              <hr />

              {/* #866 — the section used to be one flat run of four controls
                  in which "only in channels" sat between two checkboxes about
                  different things and "channel mentions" trailed the whitelist
                  it does not belong to. Grouped under headings so each
                  question ("when do channels notify me?", "when do DMs?",
                  "what never does?") is answered in one place, and so the mute
                  list has a home rather than being bolted onto the end. */}
              <h3>channels</h3>
              <label>
                <input
                  type="checkbox"
                  checked={notificationPrefs().channel_messages_all}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    togglePref(
                      "channel_messages_all",
                      (e.currentTarget as HTMLInputElement).checked,
                    )
                  }
                  data-testid="pref-channel-all"
                />
                all channel messages
              </label>
              <label class="prefs-list">
                only in channels:
                <input
                  type="text"
                  value={channelsOnlyText()}
                  disabled={notificationPrefs().channel_messages_all || savingPrefs()}
                  placeholder="#sbiffo, #grappa"
                  onInput={(e) => setChannelsOnlyText((e.currentTarget as HTMLInputElement).value)}
                  onBlur={commitChannelsOnly}
                  data-testid="pref-channels-only"
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notificationPrefs().channel_mentions}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    togglePref("channel_mentions", (e.currentTarget as HTMLInputElement).checked)
                  }
                  data-testid="pref-channel-mentions"
                />
                channel mentions
              </label>

              <h3>private messages</h3>
              <label>
                <input
                  type="checkbox"
                  checked={notificationPrefs().private_messages_all}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    togglePref(
                      "private_messages_all",
                      (e.currentTarget as HTMLInputElement).checked,
                    )
                  }
                  data-testid="pref-private-all"
                />
                all private messages
              </label>
              <label class="prefs-list">
                only from nicks:
                <input
                  type="text"
                  value={nicksOnlyText()}
                  disabled={notificationPrefs().private_messages_all || savingPrefs()}
                  placeholder="alice, bob"
                  onInput={(e) => setNicksOnlyText((e.currentTarget as HTMLInputElement).value)}
                  onBlur={commitNicksOnly}
                  data-testid="pref-nicks-only"
                />
              </label>

              {/* #378 — the /notify watch list curates WHO; these two gate
                  whether a transition also becomes an OS notification. The
                  labels say "push" on purpose: unlike every other checkbox
                  here, these gate ONLY push — the in-app toast fires either
                  way — so "watched nick comes online" would read as "notify
                  me at all". */}
              <h3>watched nicks</h3>
              <label>
                <input
                  type="checkbox"
                  checked={notificationPrefs().presence_online}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    togglePref("presence_online", (e.currentTarget as HTMLInputElement).checked)
                  }
                  data-testid="pref-presence-online"
                />
                push when a watched nick comes online
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notificationPrefs().presence_offline}
                  disabled={savingPrefs()}
                  onChange={(e) =>
                    togglePref("presence_offline", (e.currentTarget as HTMLInputElement).checked)
                  }
                  data-testid="pref-presence-offline"
                />
                push when a watched nick goes offline
              </label>

              <h3>muted conversations</h3>
              <p class="prefs-hint">
                A muted conversation never notifies — not even when someone says your nick. A mute
                belongs to one network: the same channel elsewhere stays audible.
              </p>
              <label class="prefs-list">
                mute:
                <select
                  disabled={savingPrefs() || muteCandidates().length === 0}
                  // Value resets to "" so the same conversation can be muted,
                  // removed, and muted again without the select getting stuck
                  // on a stale selection.
                  value=""
                  onChange={(e) => {
                    const el = e.currentTarget as HTMLSelectElement;
                    const picked = el.value;
                    el.value = "";
                    muteConversation(picked as ChannelKey);
                  }}
                  data-testid="pref-mute-picker"
                >
                  <option value="">pick a conversation…</option>
                  <For each={muteCandidates()}>
                    {(candidate) => <option value={candidate.key}>{candidate.label}</option>}
                  </For>
                </select>
              </label>
              <Show when={mutedConversations().length > 0}>
                <ul class="watchlists-list" data-testid="pref-muted-list">
                  <For each={mutedConversations()}>
                    {(muted) => (
                      <li class="watchlists-item" data-testid={`pref-muted-${muted.key}`}>
                        <span class="watchlists-keyword">{muted.target}</span>
                        {/* #1038 — the network the mute belongs to. Rendered
                            as its own muted span rather than folded into the
                            keyword: the CHANNEL is what the operator scans
                            for, the network is the disambiguator they only
                            need when the same name appears twice. A
                            pre-#1038 leftover the migration could not place
                            has no network and simply shows none. */}
                        <Show when={muted.network}>
                          {(network) => (
                            <span class="muted" data-testid={`pref-muted-net-${muted.key}`}>
                              {network()}
                            </span>
                          )}
                        </Show>
                        {/* #950 — a time-boxed mute (written by the rail
                            picker) says how long it has left; a permanent one
                            renders nothing extra. Computed at render from the
                            client's clock — the same clock the client-side
                            expiry filter uses — so the number the operator
                            reads and the moment cic stops silencing agree,
                            even if the server's clock does not (see
                            lib/muteSnooze.ts on the unexamined skew). */}
                        <Show when={muted.until !== null}>
                          <span class="muted" data-testid={`pref-muted-until-${muted.key}`}>
                            {formatDuration(
                              Math.max(0, (muted.until as number) - Math.floor(Date.now() / 1000)),
                            )}{" "}
                            left
                          </span>
                        </Show>
                        <button
                          type="button"
                          class="watchlists-remove"
                          disabled={savingPrefs()}
                          aria-label={`Unmute ${muted.target}${muted.network === null ? "" : ` on ${muted.network}`}`}
                          onClick={() => unmuteConversation(muted.key)}
                        >
                          ×
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>

              <Show when={prefsError() !== null}>
                <p class="prefs-error" role="alert" data-testid="prefs-error">
                  {prefsError()}
                </p>
              </Show>

              <Show when={devices().length > 0}>
                <h3>devices</h3>
                <ul class="devices-list" data-testid="devices-list">
                  <For each={deviceRows(devices())}>
                    {(row) => {
                      const d = row.device;
                      // UX-4 bucket L (2026-05-19) — replace the raw UA
                      // string with `{icon} {Browser} on {OS}`. Title
                      // attribute preserves the full UA so a hover (desktop)
                      // can still surface the original for debugging /
                      // device disambiguation across same-browser instances.
                      const parsed = parseUserAgent(d.user_agent);
                      // #964 — the always-visible disambiguators, in the order
                      // they take precedence: the user's own label, the derived
                      // ordinal on unnamed twins (both from `deviceRows`), the
                      // activity instant, and the this-device marker. Hover on
                      // the full UA is a fifth, and the only one that does not
                      // exist on touch — which is where the drawer lives, and
                      // why it was never enough on its own.
                      //
                      // The activity instant is stamped at render, not on a
                      // ticking clock: the list is refetched on drawer mount, so
                      // a live "3m ago → 4m ago" would out-freshen its own data.
                      const activity = formatDeviceActivity(d, Date.now());
                      const isCurrent = () => currentDeviceId() === d.id;
                      const editing = () => renamingId() === d.id;
                      return (
                        <li>
                          <Show
                            when={editing()}
                            fallback={
                              <>
                                <span class="device-ua" title={d.user_agent ?? "(unknown browser)"}>
                                  <span class="device-ua-icon" aria-hidden="true">
                                    {deviceClassIcon(parsed.deviceClass)}
                                  </span>
                                  <span class="device-ua-text">
                                    <span class="device-ua-title">
                                      <span class="device-ua-name" data-testid="device-name">
                                        {row.displayName}
                                      </span>
                                      <Show when={isCurrent()}>
                                        <span class="device-current" data-testid="device-current">
                                          ● this device
                                        </span>
                                      </Show>
                                    </span>
                                    {/* #964 — the parsed name survives as the
                                        secondary line ONLY once a label has
                                        taken its place above; printing it
                                        twice on an unnamed row says nothing. */}
                                    <Show when={row.named}>
                                      <span class="device-activity" data-testid="device-parsed">
                                        {deviceDisplayName(parsed)}
                                      </span>
                                    </Show>
                                    <Show when={activity !== null}>
                                      <span class="device-activity" data-testid="device-activity">
                                        {activity}
                                      </span>
                                    </Show>
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  class="device-remove"
                                  data-testid="device-rename"
                                  onClick={() => startRename(d)}
                                >
                                  rename
                                </button>
                                <button
                                  type="button"
                                  class="device-remove"
                                  onClick={() => {
                                    void removeDevice(d.id);
                                  }}
                                >
                                  remove
                                </button>
                              </>
                            }
                          >
                            <span class="device-rename-form">
                              <input
                                type="text"
                                class="device-rename-input"
                                data-testid="device-name-input"
                                aria-label="device name"
                                // Empty draft = no label yet; the placeholder shows
                                // what the row falls back to, so clearing the field
                                // is a visible choice rather than a blank row.
                                placeholder={row.displayName}
                                value={renameDraft()}
                                onInput={(e) => setRenameDraft(e.currentTarget.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void commitRename(d.id);
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    cancelRename();
                                  }
                                }}
                              />
                              <Show when={renameError() !== null}>
                                <span class="prefs-error" data-testid="device-rename-error">
                                  {renameError()}
                                </span>
                              </Show>
                            </span>
                            <button
                              type="button"
                              class="device-remove"
                              data-testid="device-rename-save"
                              onClick={() => {
                                void commitRename(d.id);
                              }}
                            >
                              save
                            </button>
                            <button
                              type="button"
                              class="device-remove"
                              data-testid="device-rename-cancel"
                              onClick={cancelRename}
                            >
                              cancel
                            </button>
                          </Show>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </Show>
            </fieldset>
          </section>
        </Show>

        {/* #252 — vhost sub-page. Replaces the main page while active; the
            server owns the allow-set + selection (cic mirrors). */}
        <Show when={settingsPage() === "vhost"}>
          <VhostSettingsPage
            view={vhostView()}
            error={vhostError()}
            onSetSelection={(addresses) => {
              void saveVhostSelection(addresses);
            }}
            onBack={() => setSettingsPage("main")}
            onReconnect={() => {
              void reconnectSession();
            }}
            reconnecting={reconnecting()}
            reconnectError={reconnectError()}
          />
        </Show>

        {/* #75 — themes gallery sub-page. Replaces the main page while
            active; the gallery owns its own server data loading. */}
        <Show when={settingsPage() === "themes"}>
          <ThemeGallery onBack={() => setSettingsPage("main")} />
        </Show>

        {/* #356 — watch lists sub-page (presence notify + keyword highlight).
            Self-contained: reads the notifyWatch + highlightList stores
            directly (like the retired home WatchedPanel), so no data props. */}
        <Show when={settingsPage() === "watchlists"}>
          <WatchlistsSettings onBack={() => setSettingsPage("main")} />
        </Show>

        {/* #162 — ignore list sub-page (per-network masks). Self-contained:
            reads the ignoreList store directly and refreshes it on open, the
            same shape as the watch lists page. */}
        <Show when={settingsPage() === "ignores"}>
          <IgnoresSettings onBack={() => setSettingsPage("main")} />
        </Show>

        {/* #385 — aliases sub-page (user-defined command aliases).
            Self-contained: reads the aliasList store directly, so no data
            props. Reached via the nav row and bare /alias. */}
        <Show when={settingsPage() === "aliases"}>
          <AliasSettings onBack={() => setSettingsPage("main")} />
        </Show>

        {/* #189 — on-connect perform list sub-page (per-network). Self-
            contained: each block loads/saves its own list via the perform
            REST endpoints. Reached via the nav row. */}
        <Show when={settingsPage() === "perform"}>
          <PerformSettings onBack={() => setSettingsPage("main")} />
        </Show>
      </aside>
      <DeleteAccountModal
        open={deleteOpen()}
        onClose={() => setDeleteOpen(false)}
        confirmationText={deleteConfirmationText()}
      />
    </>
  );
};

export default SettingsDrawer;
